import { equals } from '../../../base/common/arrays.js';
import { equals as equals$1 } from '../../../base/common/objects.js';
import { isMacintosh, isLinux } from '../../../base/common/platform.js';
import { EDITOR_MODEL_DEFAULTS } from '../core/textModelDefaults.js';
import { USUAL_WORD_SEPARATORS } from '../core/wordHelper.js';
import { localizeWithPath } from '../../../nls.js';
const _moduleId = "vs/editor/common/config/editorOptions";
const MINIMAP_GUTTER_WIDTH = 8;
class ConfigurationChangedEvent {
    constructor(values) {
        this._values = values;
    }
    hasChanged(id) {
        return this._values[id];
    }
}
class ComputeOptionsMemory {
    constructor() {
        this.stableMinimapLayoutInput = null;
        this.stableFitMaxMinimapScale = 0;
        this.stableFitRemainingWidth = 0;
    }
}
class BaseEditorOption {
    constructor(id, name, defaultValue, schema) {
        this.id = id;
        this.name = name;
        this.defaultValue = defaultValue;
        this.schema = schema;
    }
    applyUpdate(value, update) {
        return applyUpdate(value, update);
    }
    compute(env, options, value) {
        return value;
    }
}
class ApplyUpdateResult {
    constructor(newValue, didChange) {
        this.newValue = newValue;
        this.didChange = didChange;
    }
}
function applyUpdate(value, update) {
    if (typeof value !== 'object' || typeof update !== 'object' || !value || !update) {
        return (
             (new ApplyUpdateResult(update, value !== update))
        );
    }
    if (Array.isArray(value) || Array.isArray(update)) {
        const arrayEquals = Array.isArray(value) && Array.isArray(update) && equals(value, update);
        return (
             (new ApplyUpdateResult(update, !arrayEquals))
        );
    }
    let didChange = false;
    for (const key in update) {
        if (update.hasOwnProperty(key)) {
            const result = applyUpdate(value[key], update[key]);
            if (result.didChange) {
                value[key] = result.newValue;
                didChange = true;
            }
        }
    }
    return (
         (new ApplyUpdateResult(value, didChange))
    );
}
class ComputedEditorOption {
    constructor(id) {
        this.schema = undefined;
        this.id = id;
        this.name = '_never_';
        this.defaultValue = undefined;
    }
    applyUpdate(value, update) {
        return applyUpdate(value, update);
    }
    validate(input) {
        return this.defaultValue;
    }
}
class SimpleEditorOption {
    constructor(id, name, defaultValue, schema) {
        this.id = id;
        this.name = name;
        this.defaultValue = defaultValue;
        this.schema = schema;
    }
    applyUpdate(value, update) {
        return applyUpdate(value, update);
    }
    validate(input) {
        if (typeof input === 'undefined') {
            return this.defaultValue;
        }
        return input;
    }
    compute(env, options, value) {
        return value;
    }
}
function boolean(value, defaultValue) {
    if (typeof value === 'undefined') {
        return defaultValue;
    }
    if (value === 'false') {
        return false;
    }
    return Boolean(value);
}
class EditorBooleanOption extends SimpleEditorOption {
    constructor(id, name, defaultValue, schema = undefined) {
        if (typeof schema !== 'undefined') {
            schema.type = 'boolean';
            schema.default = defaultValue;
        }
        super(id, name, defaultValue, schema);
    }
    validate(input) {
        return boolean(input, this.defaultValue);
    }
}
function clampedInt(value, defaultValue, minimum, maximum) {
    if (typeof value === 'undefined') {
        return defaultValue;
    }
    let r = parseInt(value, 10);
    if (isNaN(r)) {
        return defaultValue;
    }
    r = Math.max(minimum, r);
    r = Math.min(maximum, r);
    return r | 0;
}
class EditorIntOption extends SimpleEditorOption {
    static clampedInt(value, defaultValue, minimum, maximum) {
        return clampedInt(value, defaultValue, minimum, maximum);
    }
    constructor(id, name, defaultValue, minimum, maximum, schema = undefined) {
        if (typeof schema !== 'undefined') {
            schema.type = 'integer';
            schema.default = defaultValue;
            schema.minimum = minimum;
            schema.maximum = maximum;
        }
        super(id, name, defaultValue, schema);
        this.minimum = minimum;
        this.maximum = maximum;
    }
    validate(input) {
        return EditorIntOption.clampedInt(input, this.defaultValue, this.minimum, this.maximum);
    }
}
function clampedFloat(value, defaultValue, minimum, maximum) {
    if (typeof value === 'undefined') {
        return defaultValue;
    }
    const r = EditorFloatOption.float(value, defaultValue);
    return EditorFloatOption.clamp(r, minimum, maximum);
}
class EditorFloatOption extends SimpleEditorOption {
    static clamp(n, min, max) {
        if (n < min) {
            return min;
        }
        if (n > max) {
            return max;
        }
        return n;
    }
    static float(value, defaultValue) {
        if (typeof value === 'number') {
            return value;
        }
        if (typeof value === 'undefined') {
            return defaultValue;
        }
        const r = parseFloat(value);
        return (isNaN(r) ? defaultValue : r);
    }
    constructor(id, name, defaultValue, validationFn, schema) {
        if (typeof schema !== 'undefined') {
            schema.type = 'number';
            schema.default = defaultValue;
        }
        super(id, name, defaultValue, schema);
        this.validationFn = validationFn;
    }
    validate(input) {
        return this.validationFn(EditorFloatOption.float(input, this.defaultValue));
    }
}
class EditorStringOption extends SimpleEditorOption {
    static string(value, defaultValue) {
        if (typeof value !== 'string') {
            return defaultValue;
        }
        return value;
    }
    constructor(id, name, defaultValue, schema = undefined) {
        if (typeof schema !== 'undefined') {
            schema.type = 'string';
            schema.default = defaultValue;
        }
        super(id, name, defaultValue, schema);
    }
    validate(input) {
        return EditorStringOption.string(input, this.defaultValue);
    }
}
function stringSet(value, defaultValue, allowedValues, renamedValues) {
    if (typeof value !== 'string') {
        return defaultValue;
    }
    if (renamedValues && value in renamedValues) {
        return renamedValues[value];
    }
    if (allowedValues.indexOf(value) === -1) {
        return defaultValue;
    }
    return value;
}
class EditorStringEnumOption extends SimpleEditorOption {
    constructor(id, name, defaultValue, allowedValues, schema = undefined) {
        if (typeof schema !== 'undefined') {
            schema.type = 'string';
            schema.enum = allowedValues;
            schema.default = defaultValue;
        }
        super(id, name, defaultValue, schema);
        this._allowedValues = allowedValues;
    }
    validate(input) {
        return stringSet(input, this.defaultValue, this._allowedValues);
    }
}
class EditorEnumOption extends BaseEditorOption {
    constructor(id, name, defaultValue, defaultStringValue, allowedValues, convert, schema = undefined) {
        if (typeof schema !== 'undefined') {
            schema.type = 'string';
            schema.enum = allowedValues;
            schema.default = defaultStringValue;
        }
        super(id, name, defaultValue, schema);
        this._allowedValues = allowedValues;
        this._convert = convert;
    }
    validate(input) {
        if (typeof input !== 'string') {
            return this.defaultValue;
        }
        if (this._allowedValues.indexOf(input) === -1) {
            return this.defaultValue;
        }
        return this._convert(input);
    }
}
function _autoIndentFromString(autoIndent) {
    switch (autoIndent) {
        case 'none': return 0 ;
        case 'keep': return 1 ;
        case 'brackets': return 2 ;
        case 'advanced': return 3 ;
        case 'full': return 4 ;
    }
}
class EditorAccessibilitySupport extends BaseEditorOption {
    constructor() {
        super(2 , 'accessibilitySupport', 0 , {
            type: 'string',
            enum: ['auto', 'on', 'off'],
            enumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    0,
                    "Use platform APIs to detect when a Screen Reader is attached."
                )),
                ( localizeWithPath(_moduleId, 1, "Optimize for usage with a Screen Reader.")),
                ( localizeWithPath(_moduleId, 2, "Assume a screen reader is not attached.")),
            ],
            default: 'auto',
            tags: ['accessibility'],
            description: ( localizeWithPath(
                _moduleId,
                3,
                "Controls if the UI should run in a mode where it is optimized for screen readers."
            ))
        });
    }
    validate(input) {
        switch (input) {
            case 'auto': return 0 ;
            case 'off': return 1 ;
            case 'on': return 2 ;
        }
        return this.defaultValue;
    }
    compute(env, options, value) {
        if (value === 0 ) {
            return env.accessibilitySupport;
        }
        return value;
    }
}
class EditorComments extends BaseEditorOption {
    constructor() {
        const defaults = {
            insertSpace: true,
            ignoreEmptyLines: true,
        };
        super(23 , 'comments', defaults, {
            'editor.comments.insertSpace': {
                type: 'boolean',
                default: defaults.insertSpace,
                description: ( localizeWithPath(
                    _moduleId,
                    4,
                    "Controls whether a space character is inserted when commenting."
                ))
            },
            'editor.comments.ignoreEmptyLines': {
                type: 'boolean',
                default: defaults.ignoreEmptyLines,
                description: ( localizeWithPath(
                    _moduleId,
                    5,
                    'Controls if empty lines should be ignored with toggle, add or remove actions for line comments.'
                ))
            },
        });
    }
    validate(_input) {
        if (!_input || typeof _input !== 'object') {
            return this.defaultValue;
        }
        const input = _input;
        return {
            insertSpace: boolean(input.insertSpace, this.defaultValue.insertSpace),
            ignoreEmptyLines: boolean(input.ignoreEmptyLines, this.defaultValue.ignoreEmptyLines),
        };
    }
}
function _cursorBlinkingStyleFromString(cursorBlinkingStyle) {
    switch (cursorBlinkingStyle) {
        case 'blink': return 1 ;
        case 'smooth': return 2 ;
        case 'phase': return 3 ;
        case 'expand': return 4 ;
        case 'solid': return 5 ;
    }
}
var TextEditorCursorStyle;
( ((function(TextEditorCursorStyle) {
    TextEditorCursorStyle[TextEditorCursorStyle["Line"] = 1] = "Line";
    TextEditorCursorStyle[TextEditorCursorStyle["Block"] = 2] = "Block";
    TextEditorCursorStyle[TextEditorCursorStyle["Underline"] = 3] = "Underline";
    TextEditorCursorStyle[TextEditorCursorStyle["LineThin"] = 4] = "LineThin";
    TextEditorCursorStyle[TextEditorCursorStyle["BlockOutline"] = 5] = "BlockOutline";
    TextEditorCursorStyle[TextEditorCursorStyle["UnderlineThin"] = 6] = "UnderlineThin";
})(TextEditorCursorStyle || (TextEditorCursorStyle = {}))));
function cursorStyleToString(cursorStyle) {
    switch (cursorStyle) {
        case TextEditorCursorStyle.Line: return 'line';
        case TextEditorCursorStyle.Block: return 'block';
        case TextEditorCursorStyle.Underline: return 'underline';
        case TextEditorCursorStyle.LineThin: return 'line-thin';
        case TextEditorCursorStyle.BlockOutline: return 'block-outline';
        case TextEditorCursorStyle.UnderlineThin: return 'underline-thin';
    }
}
function _cursorStyleFromString(cursorStyle) {
    switch (cursorStyle) {
        case 'line': return TextEditorCursorStyle.Line;
        case 'block': return TextEditorCursorStyle.Block;
        case 'underline': return TextEditorCursorStyle.Underline;
        case 'line-thin': return TextEditorCursorStyle.LineThin;
        case 'block-outline': return TextEditorCursorStyle.BlockOutline;
        case 'underline-thin': return TextEditorCursorStyle.UnderlineThin;
    }
}
class EditorClassName extends ComputedEditorOption {
    constructor() {
        super(142 );
    }
    compute(env, options, _) {
        const classNames = ['monaco-editor'];
        if (options.get(39 )) {
            classNames.push(options.get(39 ));
        }
        if (env.extraEditorClassName) {
            classNames.push(env.extraEditorClassName);
        }
        if (options.get(74 ) === 'default') {
            classNames.push('mouse-default');
        }
        else if (options.get(74 ) === 'copy') {
            classNames.push('mouse-copy');
        }
        if (options.get(111 )) {
            classNames.push('showUnused');
        }
        if (options.get(140 )) {
            classNames.push('showDeprecated');
        }
        return classNames.join(' ');
    }
}
class EditorEmptySelectionClipboard extends EditorBooleanOption {
    constructor() {
        super(37 , 'emptySelectionClipboard', true, { description: ( localizeWithPath(
            _moduleId,
            6,
            "Controls whether copying without a selection copies the current line."
        )) });
    }
    compute(env, options, value) {
        return value && env.emptySelectionClipboard;
    }
}
class EditorFind extends BaseEditorOption {
    constructor() {
        const defaults = {
            cursorMoveOnType: true,
            seedSearchStringFromSelection: 'always',
            autoFindInSelection: 'never',
            globalFindClipboard: false,
            addExtraSpaceOnTop: true,
            loop: true
        };
        super(41 , 'find', defaults, {
            'editor.find.cursorMoveOnType': {
                type: 'boolean',
                default: defaults.cursorMoveOnType,
                description: ( localizeWithPath(
                    _moduleId,
                    7,
                    "Controls whether the cursor should jump to find matches while typing."
                ))
            },
            'editor.find.seedSearchStringFromSelection': {
                type: 'string',
                enum: ['never', 'always', 'selection'],
                default: defaults.seedSearchStringFromSelection,
                enumDescriptions: [
                    ( localizeWithPath(_moduleId, 8, 'Never seed search string from the editor selection.')),
                    ( localizeWithPath(
                        _moduleId,
                        9,
                        'Always seed search string from the editor selection, including word at cursor position.'
                    )),
                    ( localizeWithPath(_moduleId, 10, 'Only seed search string from the editor selection.'))
                ],
                description: ( localizeWithPath(
                    _moduleId,
                    11,
                    "Controls whether the search string in the Find Widget is seeded from the editor selection."
                ))
            },
            'editor.find.autoFindInSelection': {
                type: 'string',
                enum: ['never', 'always', 'multiline'],
                default: defaults.autoFindInSelection,
                enumDescriptions: [
                    ( localizeWithPath(_moduleId, 12, 'Never turn on Find in Selection automatically (default).')),
                    ( localizeWithPath(_moduleId, 13, 'Always turn on Find in Selection automatically.')),
                    ( localizeWithPath(
                        _moduleId,
                        14,
                        'Turn on Find in Selection automatically when multiple lines of content are selected.'
                    ))
                ],
                description: ( localizeWithPath(
                    _moduleId,
                    15,
                    "Controls the condition for turning on Find in Selection automatically."
                ))
            },
            'editor.find.globalFindClipboard': {
                type: 'boolean',
                default: defaults.globalFindClipboard,
                description: ( localizeWithPath(
                    _moduleId,
                    16,
                    "Controls whether the Find Widget should read or modify the shared find clipboard on macOS."
                )),
                included: isMacintosh
            },
            'editor.find.addExtraSpaceOnTop': {
                type: 'boolean',
                default: defaults.addExtraSpaceOnTop,
                description: ( localizeWithPath(
                    _moduleId,
                    17,
                    "Controls whether the Find Widget should add extra lines on top of the editor. When true, you can scroll beyond the first line when the Find Widget is visible."
                ))
            },
            'editor.find.loop': {
                type: 'boolean',
                default: defaults.loop,
                description: ( localizeWithPath(
                    _moduleId,
                    18,
                    "Controls whether the search automatically restarts from the beginning (or the end) when no further matches can be found."
                ))
            },
        });
    }
    validate(_input) {
        if (!_input || typeof _input !== 'object') {
            return this.defaultValue;
        }
        const input = _input;
        return {
            cursorMoveOnType: boolean(input.cursorMoveOnType, this.defaultValue.cursorMoveOnType),
            seedSearchStringFromSelection: typeof _input.seedSearchStringFromSelection === 'boolean'
                ? (_input.seedSearchStringFromSelection ? 'always' : 'never')
                : stringSet(input.seedSearchStringFromSelection, this.defaultValue.seedSearchStringFromSelection, ['never', 'always', 'selection']),
            autoFindInSelection: typeof _input.autoFindInSelection === 'boolean'
                ? (_input.autoFindInSelection ? 'always' : 'never')
                : stringSet(input.autoFindInSelection, this.defaultValue.autoFindInSelection, ['never', 'always', 'multiline']),
            globalFindClipboard: boolean(input.globalFindClipboard, this.defaultValue.globalFindClipboard),
            addExtraSpaceOnTop: boolean(input.addExtraSpaceOnTop, this.defaultValue.addExtraSpaceOnTop),
            loop: boolean(input.loop, this.defaultValue.loop),
        };
    }
}
class EditorFontLigatures extends BaseEditorOption {
    static { this.OFF = '"liga" off, "calt" off'; }
    static { this.ON = '"liga" on, "calt" on'; }
    constructor() {
        super(51 , 'fontLigatures', EditorFontLigatures.OFF, {
            anyOf: [
                {
                    type: 'boolean',
                    description: ( localizeWithPath(
                        _moduleId,
                        19,
                        "Enables/Disables font ligatures ('calt' and 'liga' font features). Change this to a string for fine-grained control of the 'font-feature-settings' CSS property."
                    )),
                },
                {
                    type: 'string',
                    description: ( localizeWithPath(
                        _moduleId,
                        20,
                        "Explicit 'font-feature-settings' CSS property. A boolean can be passed instead if one only needs to turn on/off ligatures."
                    ))
                }
            ],
            description: ( localizeWithPath(
                _moduleId,
                21,
                "Configures font ligatures or font features. Can be either a boolean to enable/disable ligatures or a string for the value of the CSS 'font-feature-settings' property."
            )),
            default: false
        });
    }
    validate(input) {
        if (typeof input === 'undefined') {
            return this.defaultValue;
        }
        if (typeof input === 'string') {
            if (input === 'false' || input.length === 0) {
                return EditorFontLigatures.OFF;
            }
            if (input === 'true') {
                return EditorFontLigatures.ON;
            }
            return input;
        }
        if (Boolean(input)) {
            return EditorFontLigatures.ON;
        }
        return EditorFontLigatures.OFF;
    }
}
class EditorFontVariations extends BaseEditorOption {
    static { this.OFF = 'normal'; }
    static { this.TRANSLATE = 'translate'; }
    constructor() {
        super(54 , 'fontVariations', EditorFontVariations.OFF, {
            anyOf: [
                {
                    type: 'boolean',
                    description: ( localizeWithPath(
                        _moduleId,
                        22,
                        "Enables/Disables the translation from font-weight to font-variation-settings. Change this to a string for fine-grained control of the 'font-variation-settings' CSS property."
                    )),
                },
                {
                    type: 'string',
                    description: ( localizeWithPath(
                        _moduleId,
                        23,
                        "Explicit 'font-variation-settings' CSS property. A boolean can be passed instead if one only needs to translate font-weight to font-variation-settings."
                    ))
                }
            ],
            description: ( localizeWithPath(
                _moduleId,
                24,
                "Configures font variations. Can be either a boolean to enable/disable the translation from font-weight to font-variation-settings or a string for the value of the CSS 'font-variation-settings' property."
            )),
            default: false
        });
    }
    validate(input) {
        if (typeof input === 'undefined') {
            return this.defaultValue;
        }
        if (typeof input === 'string') {
            if (input === 'false') {
                return EditorFontVariations.OFF;
            }
            if (input === 'true') {
                return EditorFontVariations.TRANSLATE;
            }
            return input;
        }
        if (Boolean(input)) {
            return EditorFontVariations.TRANSLATE;
        }
        return EditorFontVariations.OFF;
    }
    compute(env, options, value) {
        return env.fontInfo.fontVariationSettings;
    }
}
class EditorFontInfo extends ComputedEditorOption {
    constructor() {
        super(50 );
    }
    compute(env, options, _) {
        return env.fontInfo;
    }
}
class EditorFontSize extends SimpleEditorOption {
    constructor() {
        super(52 , 'fontSize', EDITOR_FONT_DEFAULTS.fontSize, {
            type: 'number',
            minimum: 6,
            maximum: 100,
            default: EDITOR_FONT_DEFAULTS.fontSize,
            description: ( localizeWithPath(_moduleId, 25, "Controls the font size in pixels."))
        });
    }
    validate(input) {
        const r = EditorFloatOption.float(input, this.defaultValue);
        if (r === 0) {
            return EDITOR_FONT_DEFAULTS.fontSize;
        }
        return EditorFloatOption.clamp(r, 6, 100);
    }
    compute(env, options, value) {
        return env.fontInfo.fontSize;
    }
}
class EditorFontWeight extends BaseEditorOption {
    static { this.SUGGESTION_VALUES = ['normal', 'bold', '100', '200', '300', '400', '500', '600', '700', '800', '900']; }
    static { this.MINIMUM_VALUE = 1; }
    static { this.MAXIMUM_VALUE = 1000; }
    constructor() {
        super(53 , 'fontWeight', EDITOR_FONT_DEFAULTS.fontWeight, {
            anyOf: [
                {
                    type: 'number',
                    minimum: EditorFontWeight.MINIMUM_VALUE,
                    maximum: EditorFontWeight.MAXIMUM_VALUE,
                    errorMessage: ( localizeWithPath(
                        _moduleId,
                        26,
                        "Only \"normal\" and \"bold\" keywords or numbers between 1 and 1000 are allowed."
                    ))
                },
                {
                    type: 'string',
                    pattern: '^(normal|bold|1000|[1-9][0-9]{0,2})$'
                },
                {
                    enum: EditorFontWeight.SUGGESTION_VALUES
                }
            ],
            default: EDITOR_FONT_DEFAULTS.fontWeight,
            description: ( localizeWithPath(
                _moduleId,
                27,
                "Controls the font weight. Accepts \"normal\" and \"bold\" keywords or numbers between 1 and 1000."
            ))
        });
    }
    validate(input) {
        if (input === 'normal' || input === 'bold') {
            return input;
        }
        return String(EditorIntOption.clampedInt(input, EDITOR_FONT_DEFAULTS.fontWeight, EditorFontWeight.MINIMUM_VALUE, EditorFontWeight.MAXIMUM_VALUE));
    }
}
class EditorGoToLocation extends BaseEditorOption {
    constructor() {
        const defaults = {
            multiple: 'peek',
            multipleDefinitions: 'peek',
            multipleTypeDefinitions: 'peek',
            multipleDeclarations: 'peek',
            multipleImplementations: 'peek',
            multipleReferences: 'peek',
            alternativeDefinitionCommand: 'editor.action.goToReferences',
            alternativeTypeDefinitionCommand: 'editor.action.goToReferences',
            alternativeDeclarationCommand: 'editor.action.goToReferences',
            alternativeImplementationCommand: '',
            alternativeReferenceCommand: '',
        };
        const jsonSubset = {
            type: 'string',
            enum: ['peek', 'gotoAndPeek', 'goto'],
            default: defaults.multiple,
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 28, 'Show Peek view of the results (default)')),
                ( localizeWithPath(_moduleId, 29, 'Go to the primary result and show a Peek view')),
                ( localizeWithPath(
                    _moduleId,
                    30,
                    'Go to the primary result and enable Peek-less navigation to others'
                ))
            ]
        };
        const alternativeCommandOptions = ['', 'editor.action.referenceSearch.trigger', 'editor.action.goToReferences', 'editor.action.peekImplementation', 'editor.action.goToImplementation', 'editor.action.peekTypeDefinition', 'editor.action.goToTypeDefinition', 'editor.action.peekDeclaration', 'editor.action.revealDeclaration', 'editor.action.peekDefinition', 'editor.action.revealDefinitionAside', 'editor.action.revealDefinition'];
        super(58 , 'gotoLocation', defaults, {
            'editor.gotoLocation.multiple': {
                deprecationMessage: ( localizeWithPath(
                    _moduleId,
                    31,
                    "This setting is deprecated, please use separate settings like 'editor.editor.gotoLocation.multipleDefinitions' or 'editor.editor.gotoLocation.multipleImplementations' instead."
                )),
            },
            'editor.gotoLocation.multipleDefinitions': {
                description: ( localizeWithPath(
                    _moduleId,
                    32,
                    "Controls the behavior the 'Go to Definition'-command when multiple target locations exist."
                )),
                ...jsonSubset,
            },
            'editor.gotoLocation.multipleTypeDefinitions': {
                description: ( localizeWithPath(
                    _moduleId,
                    33,
                    "Controls the behavior the 'Go to Type Definition'-command when multiple target locations exist."
                )),
                ...jsonSubset,
            },
            'editor.gotoLocation.multipleDeclarations': {
                description: ( localizeWithPath(
                    _moduleId,
                    34,
                    "Controls the behavior the 'Go to Declaration'-command when multiple target locations exist."
                )),
                ...jsonSubset,
            },
            'editor.gotoLocation.multipleImplementations': {
                description: ( localizeWithPath(
                    _moduleId,
                    35,
                    "Controls the behavior the 'Go to Implementations'-command when multiple target locations exist."
                )),
                ...jsonSubset,
            },
            'editor.gotoLocation.multipleReferences': {
                description: ( localizeWithPath(
                    _moduleId,
                    36,
                    "Controls the behavior the 'Go to References'-command when multiple target locations exist."
                )),
                ...jsonSubset,
            },
            'editor.gotoLocation.alternativeDefinitionCommand': {
                type: 'string',
                default: defaults.alternativeDefinitionCommand,
                enum: alternativeCommandOptions,
                description: ( localizeWithPath(
                    _moduleId,
                    37,
                    "Alternative command id that is being executed when the result of 'Go to Definition' is the current location."
                ))
            },
            'editor.gotoLocation.alternativeTypeDefinitionCommand': {
                type: 'string',
                default: defaults.alternativeTypeDefinitionCommand,
                enum: alternativeCommandOptions,
                description: ( localizeWithPath(
                    _moduleId,
                    38,
                    "Alternative command id that is being executed when the result of 'Go to Type Definition' is the current location."
                ))
            },
            'editor.gotoLocation.alternativeDeclarationCommand': {
                type: 'string',
                default: defaults.alternativeDeclarationCommand,
                enum: alternativeCommandOptions,
                description: ( localizeWithPath(
                    _moduleId,
                    39,
                    "Alternative command id that is being executed when the result of 'Go to Declaration' is the current location."
                ))
            },
            'editor.gotoLocation.alternativeImplementationCommand': {
                type: 'string',
                default: defaults.alternativeImplementationCommand,
                enum: alternativeCommandOptions,
                description: ( localizeWithPath(
                    _moduleId,
                    40,
                    "Alternative command id that is being executed when the result of 'Go to Implementation' is the current location."
                ))
            },
            'editor.gotoLocation.alternativeReferenceCommand': {
                type: 'string',
                default: defaults.alternativeReferenceCommand,
                enum: alternativeCommandOptions,
                description: ( localizeWithPath(
                    _moduleId,
                    41,
                    "Alternative command id that is being executed when the result of 'Go to Reference' is the current location."
                ))
            },
        });
    }
    validate(_input) {
        if (!_input || typeof _input !== 'object') {
            return this.defaultValue;
        }
        const input = _input;
        return {
            multiple: stringSet(input.multiple, this.defaultValue.multiple, ['peek', 'gotoAndPeek', 'goto']),
            multipleDefinitions: input.multipleDefinitions ?? stringSet(input.multipleDefinitions, 'peek', ['peek', 'gotoAndPeek', 'goto']),
            multipleTypeDefinitions: input.multipleTypeDefinitions ?? stringSet(input.multipleTypeDefinitions, 'peek', ['peek', 'gotoAndPeek', 'goto']),
            multipleDeclarations: input.multipleDeclarations ?? stringSet(input.multipleDeclarations, 'peek', ['peek', 'gotoAndPeek', 'goto']),
            multipleImplementations: input.multipleImplementations ?? stringSet(input.multipleImplementations, 'peek', ['peek', 'gotoAndPeek', 'goto']),
            multipleReferences: input.multipleReferences ?? stringSet(input.multipleReferences, 'peek', ['peek', 'gotoAndPeek', 'goto']),
            alternativeDefinitionCommand: EditorStringOption.string(input.alternativeDefinitionCommand, this.defaultValue.alternativeDefinitionCommand),
            alternativeTypeDefinitionCommand: EditorStringOption.string(input.alternativeTypeDefinitionCommand, this.defaultValue.alternativeTypeDefinitionCommand),
            alternativeDeclarationCommand: EditorStringOption.string(input.alternativeDeclarationCommand, this.defaultValue.alternativeDeclarationCommand),
            alternativeImplementationCommand: EditorStringOption.string(input.alternativeImplementationCommand, this.defaultValue.alternativeImplementationCommand),
            alternativeReferenceCommand: EditorStringOption.string(input.alternativeReferenceCommand, this.defaultValue.alternativeReferenceCommand),
        };
    }
}
class EditorHover extends BaseEditorOption {
    constructor() {
        const defaults = {
            enabled: true,
            delay: 300,
            hidingDelay: 300,
            sticky: true,
            above: true,
        };
        super(60 , 'hover', defaults, {
            'editor.hover.enabled': {
                type: 'boolean',
                default: defaults.enabled,
                description: ( localizeWithPath(_moduleId, 42, "Controls whether the hover is shown."))
            },
            'editor.hover.delay': {
                type: 'number',
                default: defaults.delay,
                minimum: 0,
                maximum: 10000,
                description: ( localizeWithPath(
                    _moduleId,
                    43,
                    "Controls the delay in milliseconds after which the hover is shown."
                ))
            },
            'editor.hover.sticky': {
                type: 'boolean',
                default: defaults.sticky,
                description: ( localizeWithPath(
                    _moduleId,
                    44,
                    "Controls whether the hover should remain visible when mouse is moved over it."
                ))
            },
            'editor.hover.hidingDelay': {
                type: 'integer',
                minimum: 0,
                default: defaults.hidingDelay,
                description: ( localizeWithPath(
                    _moduleId,
                    45,
                    "Controls the delay in milliseconds after which the hover is hidden. Requires `editor.hover.sticky` to be enabled."
                ))
            },
            'editor.hover.above': {
                type: 'boolean',
                default: defaults.above,
                description: ( localizeWithPath(_moduleId, 46, "Prefer showing hovers above the line, if there's space."))
            },
        });
    }
    validate(_input) {
        if (!_input || typeof _input !== 'object') {
            return this.defaultValue;
        }
        const input = _input;
        return {
            enabled: boolean(input.enabled, this.defaultValue.enabled),
            delay: EditorIntOption.clampedInt(input.delay, this.defaultValue.delay, 0, 10000),
            sticky: boolean(input.sticky, this.defaultValue.sticky),
            hidingDelay: EditorIntOption.clampedInt(input.hidingDelay, this.defaultValue.hidingDelay, 0, 600000),
            above: boolean(input.above, this.defaultValue.above),
        };
    }
}
class EditorLayoutInfoComputer extends ComputedEditorOption {
    constructor() {
        super(145 );
    }
    compute(env, options, _) {
        return EditorLayoutInfoComputer.computeLayout(options, {
            memory: env.memory,
            outerWidth: env.outerWidth,
            outerHeight: env.outerHeight,
            isDominatedByLongLines: env.isDominatedByLongLines,
            lineHeight: env.fontInfo.lineHeight,
            viewLineCount: env.viewLineCount,
            lineNumbersDigitCount: env.lineNumbersDigitCount,
            typicalHalfwidthCharacterWidth: env.fontInfo.typicalHalfwidthCharacterWidth,
            maxDigitWidth: env.fontInfo.maxDigitWidth,
            pixelRatio: env.pixelRatio,
            glyphMarginDecorationLaneCount: env.glyphMarginDecorationLaneCount
        });
    }
    static computeContainedMinimapLineCount(input) {
        const typicalViewportLineCount = input.height / input.lineHeight;
        const extraLinesBeforeFirstLine = Math.floor(input.paddingTop / input.lineHeight);
        let extraLinesBeyondLastLine = Math.floor(input.paddingBottom / input.lineHeight);
        if (input.scrollBeyondLastLine) {
            extraLinesBeyondLastLine = Math.max(extraLinesBeyondLastLine, typicalViewportLineCount - 1);
        }
        const desiredRatio = (extraLinesBeforeFirstLine + input.viewLineCount + extraLinesBeyondLastLine) / (input.pixelRatio * input.height);
        const minimapLineCount = Math.floor(input.viewLineCount / desiredRatio);
        return { typicalViewportLineCount, extraLinesBeforeFirstLine, extraLinesBeyondLastLine, desiredRatio, minimapLineCount };
    }
    static _computeMinimapLayout(input, memory) {
        const outerWidth = input.outerWidth;
        const outerHeight = input.outerHeight;
        const pixelRatio = input.pixelRatio;
        if (!input.minimap.enabled) {
            return {
                renderMinimap: 0 ,
                minimapLeft: 0,
                minimapWidth: 0,
                minimapHeightIsEditorHeight: false,
                minimapIsSampling: false,
                minimapScale: 1,
                minimapLineHeight: 1,
                minimapCanvasInnerWidth: 0,
                minimapCanvasInnerHeight: Math.floor(pixelRatio * outerHeight),
                minimapCanvasOuterWidth: 0,
                minimapCanvasOuterHeight: outerHeight,
            };
        }
        const stableMinimapLayoutInput = memory.stableMinimapLayoutInput;
        const couldUseMemory = (stableMinimapLayoutInput
            && input.outerHeight === stableMinimapLayoutInput.outerHeight
            && input.lineHeight === stableMinimapLayoutInput.lineHeight
            && input.typicalHalfwidthCharacterWidth === stableMinimapLayoutInput.typicalHalfwidthCharacterWidth
            && input.pixelRatio === stableMinimapLayoutInput.pixelRatio
            && input.scrollBeyondLastLine === stableMinimapLayoutInput.scrollBeyondLastLine
            && input.paddingTop === stableMinimapLayoutInput.paddingTop
            && input.paddingBottom === stableMinimapLayoutInput.paddingBottom
            && input.minimap.enabled === stableMinimapLayoutInput.minimap.enabled
            && input.minimap.side === stableMinimapLayoutInput.minimap.side
            && input.minimap.size === stableMinimapLayoutInput.minimap.size
            && input.minimap.showSlider === stableMinimapLayoutInput.minimap.showSlider
            && input.minimap.renderCharacters === stableMinimapLayoutInput.minimap.renderCharacters
            && input.minimap.maxColumn === stableMinimapLayoutInput.minimap.maxColumn
            && input.minimap.scale === stableMinimapLayoutInput.minimap.scale
            && input.verticalScrollbarWidth === stableMinimapLayoutInput.verticalScrollbarWidth
            && input.isViewportWrapping === stableMinimapLayoutInput.isViewportWrapping);
        const lineHeight = input.lineHeight;
        const typicalHalfwidthCharacterWidth = input.typicalHalfwidthCharacterWidth;
        const scrollBeyondLastLine = input.scrollBeyondLastLine;
        const minimapRenderCharacters = input.minimap.renderCharacters;
        let minimapScale = (pixelRatio >= 2 ? Math.round(input.minimap.scale * 2) : input.minimap.scale);
        const minimapMaxColumn = input.minimap.maxColumn;
        const minimapSize = input.minimap.size;
        const minimapSide = input.minimap.side;
        const verticalScrollbarWidth = input.verticalScrollbarWidth;
        const viewLineCount = input.viewLineCount;
        const remainingWidth = input.remainingWidth;
        const isViewportWrapping = input.isViewportWrapping;
        const baseCharHeight = minimapRenderCharacters ? 2 : 3;
        let minimapCanvasInnerHeight = Math.floor(pixelRatio * outerHeight);
        const minimapCanvasOuterHeight = minimapCanvasInnerHeight / pixelRatio;
        let minimapHeightIsEditorHeight = false;
        let minimapIsSampling = false;
        let minimapLineHeight = baseCharHeight * minimapScale;
        let minimapCharWidth = minimapScale / pixelRatio;
        let minimapWidthMultiplier = 1;
        if (minimapSize === 'fill' || minimapSize === 'fit') {
            const { typicalViewportLineCount, extraLinesBeforeFirstLine, extraLinesBeyondLastLine, desiredRatio, minimapLineCount } = EditorLayoutInfoComputer.computeContainedMinimapLineCount({
                viewLineCount: viewLineCount,
                scrollBeyondLastLine: scrollBeyondLastLine,
                paddingTop: input.paddingTop,
                paddingBottom: input.paddingBottom,
                height: outerHeight,
                lineHeight: lineHeight,
                pixelRatio: pixelRatio
            });
            const ratio = viewLineCount / minimapLineCount;
            if (ratio > 1) {
                minimapHeightIsEditorHeight = true;
                minimapIsSampling = true;
                minimapScale = 1;
                minimapLineHeight = 1;
                minimapCharWidth = minimapScale / pixelRatio;
            }
            else {
                let fitBecomesFill = false;
                let maxMinimapScale = minimapScale + 1;
                if (minimapSize === 'fit') {
                    const effectiveMinimapHeight = Math.ceil((extraLinesBeforeFirstLine + viewLineCount + extraLinesBeyondLastLine) * minimapLineHeight);
                    if (isViewportWrapping && couldUseMemory && remainingWidth <= memory.stableFitRemainingWidth) {
                        fitBecomesFill = true;
                        maxMinimapScale = memory.stableFitMaxMinimapScale;
                    }
                    else {
                        fitBecomesFill = (effectiveMinimapHeight > minimapCanvasInnerHeight);
                    }
                }
                if (minimapSize === 'fill' || fitBecomesFill) {
                    minimapHeightIsEditorHeight = true;
                    const configuredMinimapScale = minimapScale;
                    minimapLineHeight = Math.min(lineHeight * pixelRatio, Math.max(1, Math.floor(1 / desiredRatio)));
                    if (isViewportWrapping && couldUseMemory && remainingWidth <= memory.stableFitRemainingWidth) {
                        maxMinimapScale = memory.stableFitMaxMinimapScale;
                    }
                    minimapScale = Math.min(maxMinimapScale, Math.max(1, Math.floor(minimapLineHeight / baseCharHeight)));
                    if (minimapScale > configuredMinimapScale) {
                        minimapWidthMultiplier = Math.min(2, minimapScale / configuredMinimapScale);
                    }
                    minimapCharWidth = minimapScale / pixelRatio / minimapWidthMultiplier;
                    minimapCanvasInnerHeight = Math.ceil((Math.max(typicalViewportLineCount, extraLinesBeforeFirstLine + viewLineCount + extraLinesBeyondLastLine)) * minimapLineHeight);
                    if (isViewportWrapping) {
                        memory.stableMinimapLayoutInput = input;
                        memory.stableFitRemainingWidth = remainingWidth;
                        memory.stableFitMaxMinimapScale = minimapScale;
                    }
                    else {
                        memory.stableMinimapLayoutInput = null;
                        memory.stableFitRemainingWidth = 0;
                    }
                }
            }
        }
        const minimapMaxWidth = Math.floor(minimapMaxColumn * minimapCharWidth);
        const minimapWidth = Math.min(minimapMaxWidth, Math.max(0, Math.floor(((remainingWidth - verticalScrollbarWidth - 2) * minimapCharWidth) / (typicalHalfwidthCharacterWidth + minimapCharWidth))) + MINIMAP_GUTTER_WIDTH);
        let minimapCanvasInnerWidth = Math.floor(pixelRatio * minimapWidth);
        const minimapCanvasOuterWidth = minimapCanvasInnerWidth / pixelRatio;
        minimapCanvasInnerWidth = Math.floor(minimapCanvasInnerWidth * minimapWidthMultiplier);
        const renderMinimap = ((minimapRenderCharacters ? 1  : 2) );
        const minimapLeft = (minimapSide === 'left' ? 0 : (outerWidth - minimapWidth - verticalScrollbarWidth));
        return {
            renderMinimap,
            minimapLeft,
            minimapWidth,
            minimapHeightIsEditorHeight,
            minimapIsSampling,
            minimapScale,
            minimapLineHeight,
            minimapCanvasInnerWidth,
            minimapCanvasInnerHeight,
            minimapCanvasOuterWidth,
            minimapCanvasOuterHeight,
        };
    }
    static computeLayout(options, env) {
        const outerWidth = env.outerWidth | 0;
        const outerHeight = env.outerHeight | 0;
        const lineHeight = env.lineHeight | 0;
        const lineNumbersDigitCount = env.lineNumbersDigitCount | 0;
        const typicalHalfwidthCharacterWidth = env.typicalHalfwidthCharacterWidth;
        const maxDigitWidth = env.maxDigitWidth;
        const pixelRatio = env.pixelRatio;
        const viewLineCount = env.viewLineCount;
        const wordWrapOverride2 = options.get(137 );
        const wordWrapOverride1 = (wordWrapOverride2 === 'inherit' ? options.get(136 ) : wordWrapOverride2);
        const wordWrap = (wordWrapOverride1 === 'inherit' ? options.get(132 ) : wordWrapOverride1);
        const wordWrapColumn = options.get(135 );
        const isDominatedByLongLines = env.isDominatedByLongLines;
        const showGlyphMargin = options.get(57 );
        const showLineNumbers = ((options.get(68 ).renderType !== 0) );
        const lineNumbersMinChars = options.get(69 );
        const scrollBeyondLastLine = options.get(105 );
        const padding = options.get(84 );
        const minimap = options.get(73 );
        const scrollbar = options.get(103 );
        const verticalScrollbarWidth = scrollbar.verticalScrollbarSize;
        const verticalScrollbarHasArrows = scrollbar.verticalHasArrows;
        const scrollbarArrowSize = scrollbar.arrowSize;
        const horizontalScrollbarHeight = scrollbar.horizontalScrollbarSize;
        const folding = options.get(43 );
        const showFoldingDecoration = options.get(110 ) !== 'never';
        let lineDecorationsWidth = options.get(66 );
        if (folding && showFoldingDecoration) {
            lineDecorationsWidth += 16;
        }
        let lineNumbersWidth = 0;
        if (showLineNumbers) {
            const digitCount = Math.max(lineNumbersDigitCount, lineNumbersMinChars);
            lineNumbersWidth = Math.round(digitCount * maxDigitWidth);
        }
        let glyphMarginWidth = 0;
        if (showGlyphMargin) {
            glyphMarginWidth = lineHeight * env.glyphMarginDecorationLaneCount;
        }
        let glyphMarginLeft = 0;
        let lineNumbersLeft = glyphMarginLeft + glyphMarginWidth;
        let decorationsLeft = lineNumbersLeft + lineNumbersWidth;
        let contentLeft = decorationsLeft + lineDecorationsWidth;
        const remainingWidth = outerWidth - glyphMarginWidth - lineNumbersWidth - lineDecorationsWidth;
        let isWordWrapMinified = false;
        let isViewportWrapping = false;
        let wrappingColumn = -1;
        if (wordWrapOverride1 === 'inherit' && isDominatedByLongLines) {
            isWordWrapMinified = true;
            isViewportWrapping = true;
        }
        else if (wordWrap === 'on' || wordWrap === 'bounded') {
            isViewportWrapping = true;
        }
        else if (wordWrap === 'wordWrapColumn') {
            wrappingColumn = wordWrapColumn;
        }
        const minimapLayout = EditorLayoutInfoComputer._computeMinimapLayout({
            outerWidth: outerWidth,
            outerHeight: outerHeight,
            lineHeight: lineHeight,
            typicalHalfwidthCharacterWidth: typicalHalfwidthCharacterWidth,
            pixelRatio: pixelRatio,
            scrollBeyondLastLine: scrollBeyondLastLine,
            paddingTop: padding.top,
            paddingBottom: padding.bottom,
            minimap: minimap,
            verticalScrollbarWidth: verticalScrollbarWidth,
            viewLineCount: viewLineCount,
            remainingWidth: remainingWidth,
            isViewportWrapping: isViewportWrapping,
        }, env.memory || ( (new ComputeOptionsMemory())));
        if (minimapLayout.renderMinimap !== 0  && minimapLayout.minimapLeft === 0) {
            glyphMarginLeft += minimapLayout.minimapWidth;
            lineNumbersLeft += minimapLayout.minimapWidth;
            decorationsLeft += minimapLayout.minimapWidth;
            contentLeft += minimapLayout.minimapWidth;
        }
        const contentWidth = remainingWidth - minimapLayout.minimapWidth;
        const viewportColumn = Math.max(1, Math.floor((contentWidth - verticalScrollbarWidth - 2) / typicalHalfwidthCharacterWidth));
        const verticalArrowSize = (verticalScrollbarHasArrows ? scrollbarArrowSize : 0);
        if (isViewportWrapping) {
            wrappingColumn = Math.max(1, viewportColumn);
            if (wordWrap === 'bounded') {
                wrappingColumn = Math.min(wrappingColumn, wordWrapColumn);
            }
        }
        return {
            width: outerWidth,
            height: outerHeight,
            glyphMarginLeft: glyphMarginLeft,
            glyphMarginWidth: glyphMarginWidth,
            glyphMarginDecorationLaneCount: env.glyphMarginDecorationLaneCount,
            lineNumbersLeft: lineNumbersLeft,
            lineNumbersWidth: lineNumbersWidth,
            decorationsLeft: decorationsLeft,
            decorationsWidth: lineDecorationsWidth,
            contentLeft: contentLeft,
            contentWidth: contentWidth,
            minimap: minimapLayout,
            viewportColumn: viewportColumn,
            isWordWrapMinified: isWordWrapMinified,
            isViewportWrapping: isViewportWrapping,
            wrappingColumn: wrappingColumn,
            verticalScrollbarWidth: verticalScrollbarWidth,
            horizontalScrollbarHeight: horizontalScrollbarHeight,
            overviewRuler: {
                top: verticalArrowSize,
                width: verticalScrollbarWidth,
                height: (outerHeight - 2 * verticalArrowSize),
                right: 0
            }
        };
    }
}
class WrappingStrategy extends BaseEditorOption {
    constructor() {
        super(139 , 'wrappingStrategy', 'simple', {
            'editor.wrappingStrategy': {
                enumDescriptions: [
                    ( localizeWithPath(
                        _moduleId,
                        47,
                        "Assumes that all characters are of the same width. This is a fast algorithm that works correctly for monospace fonts and certain scripts (like Latin characters) where glyphs are of equal width."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        48,
                        "Delegates wrapping points computation to the browser. This is a slow algorithm, that might cause freezes for large files, but it works correctly in all cases."
                    ))
                ],
                type: 'string',
                enum: ['simple', 'advanced'],
                default: 'simple',
                description: ( localizeWithPath(
                    _moduleId,
                    49,
                    "Controls the algorithm that computes wrapping points. Note that when in accessibility mode, advanced will be used for the best experience."
                ))
            }
        });
    }
    validate(input) {
        return stringSet(input, 'simple', ['simple', 'advanced']);
    }
    compute(env, options, value) {
        const accessibilitySupport = options.get(2 );
        if (accessibilitySupport === 2 ) {
            return 'advanced';
        }
        return value;
    }
}
var ShowLightbulbIconMode;
( ((function(ShowLightbulbIconMode) {
    ShowLightbulbIconMode["Off"] = "off";
    ShowLightbulbIconMode["OnCode"] = "onCode";
    ShowLightbulbIconMode["On"] = "on";
})(ShowLightbulbIconMode || (ShowLightbulbIconMode = {}))));
class EditorLightbulb extends BaseEditorOption {
    constructor() {
        const defaults = { enabled: ShowLightbulbIconMode.On };
        super(65 , 'lightbulb', defaults, {
            'editor.lightbulb.enabled': {
                type: 'string',
                tags: ['experimental'],
                enum: [ShowLightbulbIconMode.Off, ShowLightbulbIconMode.OnCode, ShowLightbulbIconMode.On],
                default: defaults.enabled,
                enumDescriptions: [
                    ( localizeWithPath(_moduleId, 50, 'Disable the code action menu.')),
                    ( localizeWithPath(
                        _moduleId,
                        51,
                        'Show the code action menu when the cursor is on lines with code.'
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        52,
                        'Show the code action menu when the cursor is on lines with code or on empty lines.'
                    )),
                ],
                description: ( localizeWithPath(_moduleId, 53, "Enables the Code Action lightbulb in the editor."))
            }
        });
    }
    validate(_input) {
        if (!_input || typeof _input !== 'object') {
            return this.defaultValue;
        }
        const input = _input;
        return {
            enabled: stringSet(input.enabled, this.defaultValue.enabled, [ShowLightbulbIconMode.Off, ShowLightbulbIconMode.OnCode, ShowLightbulbIconMode.On])
        };
    }
}
class EditorStickyScroll extends BaseEditorOption {
    constructor() {
        const defaults = { enabled: true, maxLineCount: 5, defaultModel: 'outlineModel', scrollWithEditor: true };
        super(115 , 'stickyScroll', defaults, {
            'editor.stickyScroll.enabled': {
                type: 'boolean',
                default: defaults.enabled,
                description: ( localizeWithPath(
                    _moduleId,
                    54,
                    "Shows the nested current scopes during the scroll at the top of the editor."
                )),
                tags: ['experimental']
            },
            'editor.stickyScroll.maxLineCount': {
                type: 'number',
                default: defaults.maxLineCount,
                minimum: 1,
                maximum: 20,
                description: ( localizeWithPath(_moduleId, 55, "Defines the maximum number of sticky lines to show."))
            },
            'editor.stickyScroll.defaultModel': {
                type: 'string',
                enum: ['outlineModel', 'foldingProviderModel', 'indentationModel'],
                default: defaults.defaultModel,
                description: ( localizeWithPath(
                    _moduleId,
                    56,
                    "Defines the model to use for determining which lines to stick. If the outline model does not exist, it will fall back on the folding provider model which falls back on the indentation model. This order is respected in all three cases."
                ))
            },
            'editor.stickyScroll.scrollWithEditor': {
                type: 'boolean',
                default: defaults.scrollWithEditor,
                description: ( localizeWithPath(
                    _moduleId,
                    57,
                    "Enable scrolling of Sticky Scroll with the editor's horizontal scrollbar."
                ))
            },
        });
    }
    validate(_input) {
        if (!_input || typeof _input !== 'object') {
            return this.defaultValue;
        }
        const input = _input;
        return {
            enabled: boolean(input.enabled, this.defaultValue.enabled),
            maxLineCount: EditorIntOption.clampedInt(input.maxLineCount, this.defaultValue.maxLineCount, 1, 20),
            defaultModel: stringSet(input.defaultModel, this.defaultValue.defaultModel, ['outlineModel', 'foldingProviderModel', 'indentationModel']),
            scrollWithEditor: boolean(input.scrollWithEditor, this.defaultValue.scrollWithEditor)
        };
    }
}
class EditorInlayHints extends BaseEditorOption {
    constructor() {
        const defaults = { enabled: 'on', fontSize: 0, fontFamily: '', padding: false };
        super(141 , 'inlayHints', defaults, {
            'editor.inlayHints.enabled': {
                type: 'string',
                default: defaults.enabled,
                description: ( localizeWithPath(_moduleId, 58, "Enables the inlay hints in the editor.")),
                enum: ['on', 'onUnlessPressed', 'offUnlessPressed', 'off'],
                markdownEnumDescriptions: [
                    ( localizeWithPath(_moduleId, 59, "Inlay hints are enabled")),
                    ( localizeWithPath(
                        _moduleId,
                        60,
                        "Inlay hints are showing by default and hide when holding {0}",
                        isMacintosh ? `Ctrl+Option` : `Ctrl+Alt`
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        61,
                        "Inlay hints are hidden by default and show when holding {0}",
                        isMacintosh ? `Ctrl+Option` : `Ctrl+Alt`
                    )),
                    ( localizeWithPath(_moduleId, 62, "Inlay hints are disabled")),
                ],
            },
            'editor.inlayHints.fontSize': {
                type: 'number',
                default: defaults.fontSize,
                markdownDescription: ( localizeWithPath(
                    _moduleId,
                    63,
                    "Controls font size of inlay hints in the editor. As default the {0} is used when the configured value is less than {1} or greater than the editor font size.",
                    '`#editor.fontSize#`',
                    '`5`'
                ))
            },
            'editor.inlayHints.fontFamily': {
                type: 'string',
                default: defaults.fontFamily,
                markdownDescription: ( localizeWithPath(
                    _moduleId,
                    64,
                    "Controls font family of inlay hints in the editor. When set to empty, the {0} is used.",
                    '`#editor.fontFamily#`'
                ))
            },
            'editor.inlayHints.padding': {
                type: 'boolean',
                default: defaults.padding,
                description: ( localizeWithPath(_moduleId, 65, "Enables the padding around the inlay hints in the editor."))
            }
        });
    }
    validate(_input) {
        if (!_input || typeof _input !== 'object') {
            return this.defaultValue;
        }
        const input = _input;
        if (typeof input.enabled === 'boolean') {
            input.enabled = input.enabled ? 'on' : 'off';
        }
        return {
            enabled: stringSet(input.enabled, this.defaultValue.enabled, ['on', 'off', 'offUnlessPressed', 'onUnlessPressed']),
            fontSize: EditorIntOption.clampedInt(input.fontSize, this.defaultValue.fontSize, 0, 100),
            fontFamily: EditorStringOption.string(input.fontFamily, this.defaultValue.fontFamily),
            padding: boolean(input.padding, this.defaultValue.padding)
        };
    }
}
class EditorLineDecorationsWidth extends BaseEditorOption {
    constructor() {
        super(66 , 'lineDecorationsWidth', 10);
    }
    validate(input) {
        if (typeof input === 'string' && /^\d+(\.\d+)?ch$/.test(input)) {
            const multiple = parseFloat(input.substring(0, input.length - 2));
            return -multiple;
        }
        else {
            return EditorIntOption.clampedInt(input, this.defaultValue, 0, 1000);
        }
    }
    compute(env, options, value) {
        if (value < 0) {
            return EditorIntOption.clampedInt(-value * env.fontInfo.typicalHalfwidthCharacterWidth, this.defaultValue, 0, 1000);
        }
        else {
            return value;
        }
    }
}
class EditorLineHeight extends EditorFloatOption {
    constructor() {
        super(67 , 'lineHeight', EDITOR_FONT_DEFAULTS.lineHeight, x => EditorFloatOption.clamp(x, 0, 150), { markdownDescription: ( localizeWithPath(
            _moduleId,
            66,
            "Controls the line height. \n - Use 0 to automatically compute the line height from the font size.\n - Values between 0 and 8 will be used as a multiplier with the font size.\n - Values greater than or equal to 8 will be used as effective values."
        )) });
    }
    compute(env, options, value) {
        return env.fontInfo.lineHeight;
    }
}
class EditorMinimap extends BaseEditorOption {
    constructor() {
        const defaults = {
            enabled: true,
            size: 'proportional',
            side: 'right',
            showSlider: 'mouseover',
            autohide: false,
            renderCharacters: true,
            maxColumn: 120,
            scale: 1,
            showRegionSectionHeaders: true,
            showMarkSectionHeaders: true,
            sectionHeaderFontSize: 9,
        };
        super(73 , 'minimap', defaults, {
            'editor.minimap.enabled': {
                type: 'boolean',
                default: defaults.enabled,
                description: ( localizeWithPath(_moduleId, 67, "Controls whether the minimap is shown."))
            },
            'editor.minimap.autohide': {
                type: 'boolean',
                default: defaults.autohide,
                description: ( localizeWithPath(_moduleId, 68, "Controls whether the minimap is hidden automatically."))
            },
            'editor.minimap.size': {
                type: 'string',
                enum: ['proportional', 'fill', 'fit'],
                enumDescriptions: [
                    ( localizeWithPath(
                        _moduleId,
                        69,
                        "The minimap has the same size as the editor contents (and might scroll)."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        70,
                        "The minimap will stretch or shrink as necessary to fill the height of the editor (no scrolling)."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        71,
                        "The minimap will shrink as necessary to never be larger than the editor (no scrolling)."
                    )),
                ],
                default: defaults.size,
                description: ( localizeWithPath(_moduleId, 72, "Controls the size of the minimap."))
            },
            'editor.minimap.side': {
                type: 'string',
                enum: ['left', 'right'],
                default: defaults.side,
                description: ( localizeWithPath(_moduleId, 73, "Controls the side where to render the minimap."))
            },
            'editor.minimap.showSlider': {
                type: 'string',
                enum: ['always', 'mouseover'],
                default: defaults.showSlider,
                description: ( localizeWithPath(_moduleId, 74, "Controls when the minimap slider is shown."))
            },
            'editor.minimap.scale': {
                type: 'number',
                default: defaults.scale,
                minimum: 1,
                maximum: 3,
                enum: [1, 2, 3],
                description: ( localizeWithPath(_moduleId, 75, "Scale of content drawn in the minimap: 1, 2 or 3."))
            },
            'editor.minimap.renderCharacters': {
                type: 'boolean',
                default: defaults.renderCharacters,
                description: ( localizeWithPath(
                    _moduleId,
                    76,
                    "Render the actual characters on a line as opposed to color blocks."
                ))
            },
            'editor.minimap.maxColumn': {
                type: 'number',
                default: defaults.maxColumn,
                description: ( localizeWithPath(
                    _moduleId,
                    77,
                    "Limit the width of the minimap to render at most a certain number of columns."
                ))
            },
            'editor.minimap.showRegionSectionHeaders': {
                type: 'boolean',
                default: defaults.showRegionSectionHeaders,
                description: ( localizeWithPath(
                    _moduleId,
                    78,
                    "Controls whether named regions are shown as section headers in the minimap."
                ))
            },
            'editor.minimap.showMarkSectionHeaders': {
                type: 'boolean',
                default: defaults.showMarkSectionHeaders,
                description: ( localizeWithPath(
                    _moduleId,
                    79,
                    "Controls whether MARK: comments are shown as section headers in the minimap."
                ))
            },
            'editor.minimap.sectionHeaderFontSize': {
                type: 'number',
                default: defaults.sectionHeaderFontSize,
                description: ( localizeWithPath(_moduleId, 80, "Controls the font size of section headers in the minimap."))
            }
        });
    }
    validate(_input) {
        if (!_input || typeof _input !== 'object') {
            return this.defaultValue;
        }
        const input = _input;
        return {
            enabled: boolean(input.enabled, this.defaultValue.enabled),
            autohide: boolean(input.autohide, this.defaultValue.autohide),
            size: stringSet(input.size, this.defaultValue.size, ['proportional', 'fill', 'fit']),
            side: stringSet(input.side, this.defaultValue.side, ['right', 'left']),
            showSlider: stringSet(input.showSlider, this.defaultValue.showSlider, ['always', 'mouseover']),
            renderCharacters: boolean(input.renderCharacters, this.defaultValue.renderCharacters),
            scale: EditorIntOption.clampedInt(input.scale, 1, 1, 3),
            maxColumn: EditorIntOption.clampedInt(input.maxColumn, this.defaultValue.maxColumn, 1, 10000),
            showRegionSectionHeaders: boolean(input.showRegionSectionHeaders, this.defaultValue.showRegionSectionHeaders),
            showMarkSectionHeaders: boolean(input.showMarkSectionHeaders, this.defaultValue.showMarkSectionHeaders),
            sectionHeaderFontSize: EditorFloatOption.clamp(input.sectionHeaderFontSize ?? this.defaultValue.sectionHeaderFontSize, 4, 32),
        };
    }
}
function _multiCursorModifierFromString(multiCursorModifier) {
    if (multiCursorModifier === 'ctrlCmd') {
        return (isMacintosh ? 'metaKey' : 'ctrlKey');
    }
    return 'altKey';
}
class EditorPadding extends BaseEditorOption {
    constructor() {
        super(84 , 'padding', { top: 0, bottom: 0 }, {
            'editor.padding.top': {
                type: 'number',
                default: 0,
                minimum: 0,
                maximum: 1000,
                description: ( localizeWithPath(
                    _moduleId,
                    81,
                    "Controls the amount of space between the top edge of the editor and the first line."
                ))
            },
            'editor.padding.bottom': {
                type: 'number',
                default: 0,
                minimum: 0,
                maximum: 1000,
                description: ( localizeWithPath(
                    _moduleId,
                    82,
                    "Controls the amount of space between the bottom edge of the editor and the last line."
                ))
            }
        });
    }
    validate(_input) {
        if (!_input || typeof _input !== 'object') {
            return this.defaultValue;
        }
        const input = _input;
        return {
            top: EditorIntOption.clampedInt(input.top, 0, 0, 1000),
            bottom: EditorIntOption.clampedInt(input.bottom, 0, 0, 1000)
        };
    }
}
class EditorParameterHints extends BaseEditorOption {
    constructor() {
        const defaults = {
            enabled: true,
            cycle: true
        };
        super(86 , 'parameterHints', defaults, {
            'editor.parameterHints.enabled': {
                type: 'boolean',
                default: defaults.enabled,
                description: ( localizeWithPath(
                    _moduleId,
                    83,
                    "Enables a pop-up that shows parameter documentation and type information as you type."
                ))
            },
            'editor.parameterHints.cycle': {
                type: 'boolean',
                default: defaults.cycle,
                description: ( localizeWithPath(
                    _moduleId,
                    84,
                    "Controls whether the parameter hints menu cycles or closes when reaching the end of the list."
                ))
            },
        });
    }
    validate(_input) {
        if (!_input || typeof _input !== 'object') {
            return this.defaultValue;
        }
        const input = _input;
        return {
            enabled: boolean(input.enabled, this.defaultValue.enabled),
            cycle: boolean(input.cycle, this.defaultValue.cycle)
        };
    }
}
class EditorPixelRatio extends ComputedEditorOption {
    constructor() {
        super(143 );
    }
    compute(env, options, _) {
        return env.pixelRatio;
    }
}
class EditorQuickSuggestions extends BaseEditorOption {
    constructor() {
        const defaults = {
            other: 'on',
            comments: 'off',
            strings: 'off'
        };
        const types = [
            { type: 'boolean' },
            {
                type: 'string',
                enum: ['on', 'inline', 'off'],
                enumDescriptions: [( localizeWithPath(_moduleId, 85, "Quick suggestions show inside the suggest widget")), ( localizeWithPath(_moduleId, 86, "Quick suggestions show as ghost text")), ( localizeWithPath(_moduleId, 87, "Quick suggestions are disabled"))]
            }
        ];
        super(89 , 'quickSuggestions', defaults, {
            type: 'object',
            additionalProperties: false,
            properties: {
                strings: {
                    anyOf: types,
                    default: defaults.strings,
                    description: ( localizeWithPath(_moduleId, 88, "Enable quick suggestions inside strings."))
                },
                comments: {
                    anyOf: types,
                    default: defaults.comments,
                    description: ( localizeWithPath(_moduleId, 89, "Enable quick suggestions inside comments."))
                },
                other: {
                    anyOf: types,
                    default: defaults.other,
                    description: ( localizeWithPath(_moduleId, 90, "Enable quick suggestions outside of strings and comments."))
                },
            },
            default: defaults,
            markdownDescription: ( localizeWithPath(
                _moduleId,
                91,
                "Controls whether suggestions should automatically show up while typing. This can be controlled for typing in comments, strings, and other code. Quick suggestion can be configured to show as ghost text or with the suggest widget. Also be aware of the '{0}'-setting which controls if suggestions are triggered by special characters.",
                `#editor.suggestOnTriggerCharacters#`
            ))
        });
        this.defaultValue = defaults;
    }
    validate(input) {
        if (typeof input === 'boolean') {
            const value = input ? 'on' : 'off';
            return { comments: value, strings: value, other: value };
        }
        if (!input || typeof input !== 'object') {
            return this.defaultValue;
        }
        const { other, comments, strings } = input;
        const allowedValues = ['on', 'inline', 'off'];
        let validatedOther;
        let validatedComments;
        let validatedStrings;
        if (typeof other === 'boolean') {
            validatedOther = other ? 'on' : 'off';
        }
        else {
            validatedOther = stringSet(other, this.defaultValue.other, allowedValues);
        }
        if (typeof comments === 'boolean') {
            validatedComments = comments ? 'on' : 'off';
        }
        else {
            validatedComments = stringSet(comments, this.defaultValue.comments, allowedValues);
        }
        if (typeof strings === 'boolean') {
            validatedStrings = strings ? 'on' : 'off';
        }
        else {
            validatedStrings = stringSet(strings, this.defaultValue.strings, allowedValues);
        }
        return {
            other: validatedOther,
            comments: validatedComments,
            strings: validatedStrings
        };
    }
}
class EditorRenderLineNumbersOption extends BaseEditorOption {
    constructor() {
        super(68 , 'lineNumbers', { renderType: 1 , renderFn: null }, {
            type: 'string',
            enum: ['off', 'on', 'relative', 'interval'],
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 92, "Line numbers are not rendered.")),
                ( localizeWithPath(_moduleId, 93, "Line numbers are rendered as absolute number.")),
                ( localizeWithPath(
                    _moduleId,
                    94,
                    "Line numbers are rendered as distance in lines to cursor position."
                )),
                ( localizeWithPath(_moduleId, 95, "Line numbers are rendered every 10 lines."))
            ],
            default: 'on',
            description: ( localizeWithPath(_moduleId, 96, "Controls the display of line numbers."))
        });
    }
    validate(lineNumbers) {
        let renderType = this.defaultValue.renderType;
        let renderFn = this.defaultValue.renderFn;
        if (typeof lineNumbers !== 'undefined') {
            if (typeof lineNumbers === 'function') {
                renderType = 4 ;
                renderFn = lineNumbers;
            }
            else if (lineNumbers === 'interval') {
                renderType = 3 ;
            }
            else if (lineNumbers === 'relative') {
                renderType = 2 ;
            }
            else if (lineNumbers === 'on') {
                renderType = 1 ;
            }
            else {
                renderType = 0 ;
            }
        }
        return {
            renderType,
            renderFn
        };
    }
}
function filterValidationDecorations(options) {
    const renderValidationDecorations = options.get(98 );
    if (renderValidationDecorations === 'editable') {
        return options.get(91 );
    }
    return renderValidationDecorations === 'on' ? false : true;
}
class EditorRulers extends BaseEditorOption {
    constructor() {
        const defaults = [];
        const columnSchema = { type: 'number', description: ( localizeWithPath(
            _moduleId,
            97,
            "Number of monospace characters at which this editor ruler will render."
        )) };
        super(102 , 'rulers', defaults, {
            type: 'array',
            items: {
                anyOf: [
                    columnSchema,
                    {
                        type: [
                            'object'
                        ],
                        properties: {
                            column: columnSchema,
                            color: {
                                type: 'string',
                                description: ( localizeWithPath(_moduleId, 98, "Color of this editor ruler.")),
                                format: 'color-hex'
                            }
                        }
                    }
                ]
            },
            default: defaults,
            description: ( localizeWithPath(
                _moduleId,
                99,
                "Render vertical rulers after a certain number of monospace characters. Use multiple values for multiple rulers. No rulers are drawn if array is empty."
            ))
        });
    }
    validate(input) {
        if (Array.isArray(input)) {
            const rulers = [];
            for (const _element of input) {
                if (typeof _element === 'number') {
                    rulers.push({
                        column: EditorIntOption.clampedInt(_element, 0, 0, 10000),
                        color: null
                    });
                }
                else if (_element && typeof _element === 'object') {
                    const element = _element;
                    rulers.push({
                        column: EditorIntOption.clampedInt(element.column, 0, 0, 10000),
                        color: element.color
                    });
                }
            }
            rulers.sort((a, b) => a.column - b.column);
            return rulers;
        }
        return this.defaultValue;
    }
}
class ReadonlyMessage extends BaseEditorOption {
    constructor() {
        const defaults = undefined;
        super(92 , 'readOnlyMessage', defaults);
    }
    validate(_input) {
        if (!_input || typeof _input !== 'object') {
            return this.defaultValue;
        }
        return _input;
    }
}
function _scrollbarVisibilityFromString(visibility, defaultValue) {
    if (typeof visibility !== 'string') {
        return defaultValue;
    }
    switch (visibility) {
        case 'hidden': return 2 ;
        case 'visible': return 3 ;
        default: return 1 ;
    }
}
class EditorScrollbar extends BaseEditorOption {
    constructor() {
        const defaults = {
            vertical: 1 ,
            horizontal: 1 ,
            arrowSize: 11,
            useShadows: true,
            verticalHasArrows: false,
            horizontalHasArrows: false,
            horizontalScrollbarSize: 12,
            horizontalSliderSize: 12,
            verticalScrollbarSize: 14,
            verticalSliderSize: 14,
            handleMouseWheel: true,
            alwaysConsumeMouseWheel: true,
            scrollByPage: false,
            ignoreHorizontalScrollbarInContentHeight: false,
        };
        super(103 , 'scrollbar', defaults, {
            'editor.scrollbar.vertical': {
                type: 'string',
                enum: ['auto', 'visible', 'hidden'],
                enumDescriptions: [
                    ( localizeWithPath(
                        _moduleId,
                        100,
                        "The vertical scrollbar will be visible only when necessary."
                    )),
                    ( localizeWithPath(_moduleId, 101, "The vertical scrollbar will always be visible.")),
                    ( localizeWithPath(_moduleId, 102, "The vertical scrollbar will always be hidden.")),
                ],
                default: 'auto',
                description: ( localizeWithPath(_moduleId, 103, "Controls the visibility of the vertical scrollbar."))
            },
            'editor.scrollbar.horizontal': {
                type: 'string',
                enum: ['auto', 'visible', 'hidden'],
                enumDescriptions: [
                    ( localizeWithPath(
                        _moduleId,
                        104,
                        "The horizontal scrollbar will be visible only when necessary."
                    )),
                    ( localizeWithPath(_moduleId, 105, "The horizontal scrollbar will always be visible.")),
                    ( localizeWithPath(_moduleId, 106, "The horizontal scrollbar will always be hidden.")),
                ],
                default: 'auto',
                description: ( localizeWithPath(_moduleId, 107, "Controls the visibility of the horizontal scrollbar."))
            },
            'editor.scrollbar.verticalScrollbarSize': {
                type: 'number',
                default: defaults.verticalScrollbarSize,
                description: ( localizeWithPath(_moduleId, 108, "The width of the vertical scrollbar."))
            },
            'editor.scrollbar.horizontalScrollbarSize': {
                type: 'number',
                default: defaults.horizontalScrollbarSize,
                description: ( localizeWithPath(_moduleId, 109, "The height of the horizontal scrollbar."))
            },
            'editor.scrollbar.scrollByPage': {
                type: 'boolean',
                default: defaults.scrollByPage,
                description: ( localizeWithPath(
                    _moduleId,
                    110,
                    "Controls whether clicks scroll by page or jump to click position."
                ))
            },
            'editor.scrollbar.ignoreHorizontalScrollbarInContentHeight': {
                type: 'boolean',
                default: defaults.ignoreHorizontalScrollbarInContentHeight,
                description: ( localizeWithPath(
                    _moduleId,
                    111,
                    "When set, the horizontal scrollbar will not increase the size of the editor's content."
                ))
            }
        });
    }
    validate(_input) {
        if (!_input || typeof _input !== 'object') {
            return this.defaultValue;
        }
        const input = _input;
        const horizontalScrollbarSize = EditorIntOption.clampedInt(input.horizontalScrollbarSize, this.defaultValue.horizontalScrollbarSize, 0, 1000);
        const verticalScrollbarSize = EditorIntOption.clampedInt(input.verticalScrollbarSize, this.defaultValue.verticalScrollbarSize, 0, 1000);
        return {
            arrowSize: EditorIntOption.clampedInt(input.arrowSize, this.defaultValue.arrowSize, 0, 1000),
            vertical: _scrollbarVisibilityFromString(input.vertical, this.defaultValue.vertical),
            horizontal: _scrollbarVisibilityFromString(input.horizontal, this.defaultValue.horizontal),
            useShadows: boolean(input.useShadows, this.defaultValue.useShadows),
            verticalHasArrows: boolean(input.verticalHasArrows, this.defaultValue.verticalHasArrows),
            horizontalHasArrows: boolean(input.horizontalHasArrows, this.defaultValue.horizontalHasArrows),
            handleMouseWheel: boolean(input.handleMouseWheel, this.defaultValue.handleMouseWheel),
            alwaysConsumeMouseWheel: boolean(input.alwaysConsumeMouseWheel, this.defaultValue.alwaysConsumeMouseWheel),
            horizontalScrollbarSize: horizontalScrollbarSize,
            horizontalSliderSize: EditorIntOption.clampedInt(input.horizontalSliderSize, horizontalScrollbarSize, 0, 1000),
            verticalScrollbarSize: verticalScrollbarSize,
            verticalSliderSize: EditorIntOption.clampedInt(input.verticalSliderSize, verticalScrollbarSize, 0, 1000),
            scrollByPage: boolean(input.scrollByPage, this.defaultValue.scrollByPage),
            ignoreHorizontalScrollbarInContentHeight: boolean(input.ignoreHorizontalScrollbarInContentHeight, this.defaultValue.ignoreHorizontalScrollbarInContentHeight),
        };
    }
}
const inUntrustedWorkspace = 'inUntrustedWorkspace';
const unicodeHighlightConfigKeys = {
    allowedCharacters: 'editor.unicodeHighlight.allowedCharacters',
    invisibleCharacters: 'editor.unicodeHighlight.invisibleCharacters',
    nonBasicASCII: 'editor.unicodeHighlight.nonBasicASCII',
    ambiguousCharacters: 'editor.unicodeHighlight.ambiguousCharacters',
    includeComments: 'editor.unicodeHighlight.includeComments',
    includeStrings: 'editor.unicodeHighlight.includeStrings',
    allowedLocales: 'editor.unicodeHighlight.allowedLocales',
};
class UnicodeHighlight extends BaseEditorOption {
    constructor() {
        const defaults = {
            nonBasicASCII: inUntrustedWorkspace,
            invisibleCharacters: true,
            ambiguousCharacters: true,
            includeComments: inUntrustedWorkspace,
            includeStrings: true,
            allowedCharacters: {},
            allowedLocales: { _os: true, _vscode: true },
        };
        super(125 , 'unicodeHighlight', defaults, {
            [unicodeHighlightConfigKeys.nonBasicASCII]: {
                restricted: true,
                type: ['boolean', 'string'],
                enum: [true, false, inUntrustedWorkspace],
                default: defaults.nonBasicASCII,
                description: ( localizeWithPath(
                    _moduleId,
                    112,
                    "Controls whether all non-basic ASCII characters are highlighted. Only characters between U+0020 and U+007E, tab, line-feed and carriage-return are considered basic ASCII."
                ))
            },
            [unicodeHighlightConfigKeys.invisibleCharacters]: {
                restricted: true,
                type: 'boolean',
                default: defaults.invisibleCharacters,
                description: ( localizeWithPath(
                    _moduleId,
                    113,
                    "Controls whether characters that just reserve space or have no width at all are highlighted."
                ))
            },
            [unicodeHighlightConfigKeys.ambiguousCharacters]: {
                restricted: true,
                type: 'boolean',
                default: defaults.ambiguousCharacters,
                description: ( localizeWithPath(
                    _moduleId,
                    114,
                    "Controls whether characters are highlighted that can be confused with basic ASCII characters, except those that are common in the current user locale."
                ))
            },
            [unicodeHighlightConfigKeys.includeComments]: {
                restricted: true,
                type: ['boolean', 'string'],
                enum: [true, false, inUntrustedWorkspace],
                default: defaults.includeComments,
                description: ( localizeWithPath(
                    _moduleId,
                    115,
                    "Controls whether characters in comments should also be subject to Unicode highlighting."
                ))
            },
            [unicodeHighlightConfigKeys.includeStrings]: {
                restricted: true,
                type: ['boolean', 'string'],
                enum: [true, false, inUntrustedWorkspace],
                default: defaults.includeStrings,
                description: ( localizeWithPath(
                    _moduleId,
                    116,
                    "Controls whether characters in strings should also be subject to Unicode highlighting."
                ))
            },
            [unicodeHighlightConfigKeys.allowedCharacters]: {
                restricted: true,
                type: 'object',
                default: defaults.allowedCharacters,
                description: ( localizeWithPath(
                    _moduleId,
                    117,
                    "Defines allowed characters that are not being highlighted."
                )),
                additionalProperties: {
                    type: 'boolean'
                }
            },
            [unicodeHighlightConfigKeys.allowedLocales]: {
                restricted: true,
                type: 'object',
                additionalProperties: {
                    type: 'boolean'
                },
                default: defaults.allowedLocales,
                description: ( localizeWithPath(
                    _moduleId,
                    118,
                    "Unicode characters that are common in allowed locales are not being highlighted."
                ))
            },
        });
    }
    applyUpdate(value, update) {
        let didChange = false;
        if (update.allowedCharacters && value) {
            if (!equals$1(value.allowedCharacters, update.allowedCharacters)) {
                value = { ...value, allowedCharacters: update.allowedCharacters };
                didChange = true;
            }
        }
        if (update.allowedLocales && value) {
            if (!equals$1(value.allowedLocales, update.allowedLocales)) {
                value = { ...value, allowedLocales: update.allowedLocales };
                didChange = true;
            }
        }
        const result = super.applyUpdate(value, update);
        if (didChange) {
            return (
                 (new ApplyUpdateResult(result.newValue, true))
            );
        }
        return result;
    }
    validate(_input) {
        if (!_input || typeof _input !== 'object') {
            return this.defaultValue;
        }
        const input = _input;
        return {
            nonBasicASCII: primitiveSet(input.nonBasicASCII, inUntrustedWorkspace, [true, false, inUntrustedWorkspace]),
            invisibleCharacters: boolean(input.invisibleCharacters, this.defaultValue.invisibleCharacters),
            ambiguousCharacters: boolean(input.ambiguousCharacters, this.defaultValue.ambiguousCharacters),
            includeComments: primitiveSet(input.includeComments, inUntrustedWorkspace, [true, false, inUntrustedWorkspace]),
            includeStrings: primitiveSet(input.includeStrings, inUntrustedWorkspace, [true, false, inUntrustedWorkspace]),
            allowedCharacters: this.validateBooleanMap(_input.allowedCharacters, this.defaultValue.allowedCharacters),
            allowedLocales: this.validateBooleanMap(_input.allowedLocales, this.defaultValue.allowedLocales),
        };
    }
    validateBooleanMap(map, defaultValue) {
        if ((typeof map !== 'object') || !map) {
            return defaultValue;
        }
        const result = {};
        for (const [key, value] of Object.entries(map)) {
            if (value === true) {
                result[key] = true;
            }
        }
        return result;
    }
}
class InlineEditorSuggest extends BaseEditorOption {
    constructor() {
        const defaults = {
            enabled: true,
            mode: 'subwordSmart',
            showToolbar: 'onHover',
            suppressSuggestions: false,
            keepOnBlur: false,
            fontFamily: 'default'
        };
        super(62 , 'inlineSuggest', defaults, {
            'editor.inlineSuggest.enabled': {
                type: 'boolean',
                default: defaults.enabled,
                description: ( localizeWithPath(
                    _moduleId,
                    119,
                    "Controls whether to automatically show inline suggestions in the editor."
                ))
            },
            'editor.inlineSuggest.showToolbar': {
                type: 'string',
                default: defaults.showToolbar,
                enum: ['always', 'onHover', 'never'],
                enumDescriptions: [
                    ( localizeWithPath(
                        _moduleId,
                        120,
                        "Show the inline suggestion toolbar whenever an inline suggestion is shown."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        121,
                        "Show the inline suggestion toolbar when hovering over an inline suggestion."
                    )),
                    ( localizeWithPath(_moduleId, 122, "Never show the inline suggestion toolbar.")),
                ],
                description: ( localizeWithPath(_moduleId, 123, "Controls when to show the inline suggestion toolbar.")),
            },
            'editor.inlineSuggest.suppressSuggestions': {
                type: 'boolean',
                default: defaults.suppressSuggestions,
                description: ( localizeWithPath(
                    _moduleId,
                    124,
                    "Controls how inline suggestions interact with the suggest widget. If enabled, the suggest widget is not shown automatically when inline suggestions are available."
                ))
            },
            'editor.inlineSuggest.fontFamily': {
                type: 'string',
                default: defaults.fontFamily,
                description: ( localizeWithPath(_moduleId, 125, "Controls the font family of the inline suggestions."))
            },
        });
    }
    validate(_input) {
        if (!_input || typeof _input !== 'object') {
            return this.defaultValue;
        }
        const input = _input;
        return {
            enabled: boolean(input.enabled, this.defaultValue.enabled),
            mode: stringSet(input.mode, this.defaultValue.mode, ['prefix', 'subword', 'subwordSmart']),
            showToolbar: stringSet(input.showToolbar, this.defaultValue.showToolbar, ['always', 'onHover', 'never']),
            suppressSuggestions: boolean(input.suppressSuggestions, this.defaultValue.suppressSuggestions),
            keepOnBlur: boolean(input.keepOnBlur, this.defaultValue.keepOnBlur),
            fontFamily: EditorStringOption.string(input.fontFamily, this.defaultValue.fontFamily)
        };
    }
}
class InlineEditorEdit extends BaseEditorOption {
    constructor() {
        const defaults = {
            enabled: false,
            showToolbar: 'onHover',
            fontFamily: 'default',
            keepOnBlur: false,
            backgroundColoring: false,
        };
        super(63 , 'experimentalInlineEdit', defaults, {
            'editor.experimentalInlineEdit.enabled': {
                type: 'boolean',
                default: defaults.enabled,
                description: ( localizeWithPath(_moduleId, 126, "Controls whether to show inline edits in the editor."))
            },
            'editor.experimentalInlineEdit.showToolbar': {
                type: 'string',
                default: defaults.showToolbar,
                enum: ['always', 'onHover', 'never'],
                enumDescriptions: [
                    ( localizeWithPath(
                        _moduleId,
                        127,
                        "Show the inline edit toolbar whenever an inline suggestion is shown."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        128,
                        "Show the inline edit toolbar when hovering over an inline suggestion."
                    )),
                    ( localizeWithPath(_moduleId, 129, "Never show the inline edit toolbar.")),
                ],
                description: ( localizeWithPath(_moduleId, 130, "Controls when to show the inline edit toolbar.")),
            },
            'editor.experimentalInlineEdit.fontFamily': {
                type: 'string',
                default: defaults.fontFamily,
                description: ( localizeWithPath(_moduleId, 131, "Controls the font family of the inline edit."))
            },
            'editor.experimentalInlineEdit.backgroundColoring': {
                type: 'boolean',
                default: defaults.backgroundColoring,
                description: ( localizeWithPath(
                    _moduleId,
                    132,
                    "Controls whether to color the background of inline edits."
                ))
            },
        });
    }
    validate(_input) {
        if (!_input || typeof _input !== 'object') {
            return this.defaultValue;
        }
        const input = _input;
        return {
            enabled: boolean(input.enabled, this.defaultValue.enabled),
            showToolbar: stringSet(input.showToolbar, this.defaultValue.showToolbar, ['always', 'onHover', 'never']),
            fontFamily: EditorStringOption.string(input.fontFamily, this.defaultValue.fontFamily),
            keepOnBlur: boolean(input.keepOnBlur, this.defaultValue.keepOnBlur),
            backgroundColoring: boolean(input.backgroundColoring, this.defaultValue.backgroundColoring)
        };
    }
}
class BracketPairColorization extends BaseEditorOption {
    constructor() {
        const defaults = {
            enabled: EDITOR_MODEL_DEFAULTS.bracketPairColorizationOptions.enabled,
            independentColorPoolPerBracketType: EDITOR_MODEL_DEFAULTS.bracketPairColorizationOptions.independentColorPoolPerBracketType,
        };
        super(15 , 'bracketPairColorization', defaults, {
            'editor.bracketPairColorization.enabled': {
                type: 'boolean',
                default: defaults.enabled,
                markdownDescription: ( localizeWithPath(
                    _moduleId,
                    133,
                    "Controls whether bracket pair colorization is enabled or not. Use {0} to override the bracket highlight colors.",
                    '`#workbench.colorCustomizations#`'
                ))
            },
            'editor.bracketPairColorization.independentColorPoolPerBracketType': {
                type: 'boolean',
                default: defaults.independentColorPoolPerBracketType,
                description: ( localizeWithPath(
                    _moduleId,
                    134,
                    "Controls whether each bracket type has its own independent color pool."
                ))
            },
        });
    }
    validate(_input) {
        if (!_input || typeof _input !== 'object') {
            return this.defaultValue;
        }
        const input = _input;
        return {
            enabled: boolean(input.enabled, this.defaultValue.enabled),
            independentColorPoolPerBracketType: boolean(input.independentColorPoolPerBracketType, this.defaultValue.independentColorPoolPerBracketType),
        };
    }
}
class GuideOptions extends BaseEditorOption {
    constructor() {
        const defaults = {
            bracketPairs: false,
            bracketPairsHorizontal: 'active',
            highlightActiveBracketPair: true,
            indentation: true,
            highlightActiveIndentation: true
        };
        super(16 , 'guides', defaults, {
            'editor.guides.bracketPairs': {
                type: ['boolean', 'string'],
                enum: [true, 'active', false],
                enumDescriptions: [
                    ( localizeWithPath(_moduleId, 135, "Enables bracket pair guides.")),
                    ( localizeWithPath(
                        _moduleId,
                        136,
                        "Enables bracket pair guides only for the active bracket pair."
                    )),
                    ( localizeWithPath(_moduleId, 137, "Disables bracket pair guides.")),
                ],
                default: defaults.bracketPairs,
                description: ( localizeWithPath(_moduleId, 138, "Controls whether bracket pair guides are enabled or not."))
            },
            'editor.guides.bracketPairsHorizontal': {
                type: ['boolean', 'string'],
                enum: [true, 'active', false],
                enumDescriptions: [
                    ( localizeWithPath(
                        _moduleId,
                        139,
                        "Enables horizontal guides as addition to vertical bracket pair guides."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        140,
                        "Enables horizontal guides only for the active bracket pair."
                    )),
                    ( localizeWithPath(_moduleId, 141, "Disables horizontal bracket pair guides.")),
                ],
                default: defaults.bracketPairsHorizontal,
                description: ( localizeWithPath(
                    _moduleId,
                    142,
                    "Controls whether horizontal bracket pair guides are enabled or not."
                ))
            },
            'editor.guides.highlightActiveBracketPair': {
                type: 'boolean',
                default: defaults.highlightActiveBracketPair,
                description: ( localizeWithPath(
                    _moduleId,
                    143,
                    "Controls whether the editor should highlight the active bracket pair."
                ))
            },
            'editor.guides.indentation': {
                type: 'boolean',
                default: defaults.indentation,
                description: ( localizeWithPath(_moduleId, 144, "Controls whether the editor should render indent guides."))
            },
            'editor.guides.highlightActiveIndentation': {
                type: ['boolean', 'string'],
                enum: [true, 'always', false],
                enumDescriptions: [
                    ( localizeWithPath(_moduleId, 145, "Highlights the active indent guide.")),
                    ( localizeWithPath(
                        _moduleId,
                        146,
                        "Highlights the active indent guide even if bracket guides are highlighted."
                    )),
                    ( localizeWithPath(_moduleId, 147, "Do not highlight the active indent guide.")),
                ],
                default: defaults.highlightActiveIndentation,
                description: ( localizeWithPath(
                    _moduleId,
                    148,
                    "Controls whether the editor should highlight the active indent guide."
                ))
            }
        });
    }
    validate(_input) {
        if (!_input || typeof _input !== 'object') {
            return this.defaultValue;
        }
        const input = _input;
        return {
            bracketPairs: primitiveSet(input.bracketPairs, this.defaultValue.bracketPairs, [true, false, 'active']),
            bracketPairsHorizontal: primitiveSet(input.bracketPairsHorizontal, this.defaultValue.bracketPairsHorizontal, [true, false, 'active']),
            highlightActiveBracketPair: boolean(input.highlightActiveBracketPair, this.defaultValue.highlightActiveBracketPair),
            indentation: boolean(input.indentation, this.defaultValue.indentation),
            highlightActiveIndentation: primitiveSet(input.highlightActiveIndentation, this.defaultValue.highlightActiveIndentation, [true, false, 'always']),
        };
    }
}
function primitiveSet(value, defaultValue, allowedValues) {
    const idx = allowedValues.indexOf(value);
    if (idx === -1) {
        return defaultValue;
    }
    return allowedValues[idx];
}
class EditorSuggest extends BaseEditorOption {
    constructor() {
        const defaults = {
            insertMode: 'insert',
            filterGraceful: true,
            snippetsPreventQuickSuggestions: false,
            localityBonus: false,
            shareSuggestSelections: false,
            selectionMode: 'always',
            showIcons: true,
            showStatusBar: false,
            preview: false,
            previewMode: 'subwordSmart',
            showInlineDetails: true,
            showMethods: true,
            showFunctions: true,
            showConstructors: true,
            showDeprecated: true,
            matchOnWordStartOnly: true,
            showFields: true,
            showVariables: true,
            showClasses: true,
            showStructs: true,
            showInterfaces: true,
            showModules: true,
            showProperties: true,
            showEvents: true,
            showOperators: true,
            showUnits: true,
            showValues: true,
            showConstants: true,
            showEnums: true,
            showEnumMembers: true,
            showKeywords: true,
            showWords: true,
            showColors: true,
            showFiles: true,
            showReferences: true,
            showFolders: true,
            showTypeParameters: true,
            showSnippets: true,
            showUsers: true,
            showIssues: true,
        };
        super(118 , 'suggest', defaults, {
            'editor.suggest.insertMode': {
                type: 'string',
                enum: ['insert', 'replace'],
                enumDescriptions: [
                    ( localizeWithPath(
                        _moduleId,
                        149,
                        "Insert suggestion without overwriting text right of the cursor."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        150,
                        "Insert suggestion and overwrite text right of the cursor."
                    )),
                ],
                default: defaults.insertMode,
                description: ( localizeWithPath(
                    _moduleId,
                    151,
                    "Controls whether words are overwritten when accepting completions. Note that this depends on extensions opting into this feature."
                ))
            },
            'editor.suggest.filterGraceful': {
                type: 'boolean',
                default: defaults.filterGraceful,
                description: ( localizeWithPath(
                    _moduleId,
                    152,
                    "Controls whether filtering and sorting suggestions accounts for small typos."
                ))
            },
            'editor.suggest.localityBonus': {
                type: 'boolean',
                default: defaults.localityBonus,
                description: ( localizeWithPath(
                    _moduleId,
                    153,
                    "Controls whether sorting favors words that appear close to the cursor."
                ))
            },
            'editor.suggest.shareSuggestSelections': {
                type: 'boolean',
                default: defaults.shareSuggestSelections,
                markdownDescription: ( localizeWithPath(
                    _moduleId,
                    154,
                    "Controls whether remembered suggestion selections are shared between multiple workspaces and windows (needs `#editor.suggestSelection#`)."
                ))
            },
            'editor.suggest.selectionMode': {
                type: 'string',
                enum: ['always', 'never', 'whenTriggerCharacter', 'whenQuickSuggestion'],
                enumDescriptions: [
                    ( localizeWithPath(
                        _moduleId,
                        155,
                        "Always select a suggestion when automatically triggering IntelliSense."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        156,
                        "Never select a suggestion when automatically triggering IntelliSense."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        157,
                        "Select a suggestion only when triggering IntelliSense from a trigger character."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        158,
                        "Select a suggestion only when triggering IntelliSense as you type."
                    )),
                ],
                default: defaults.selectionMode,
                markdownDescription: ( localizeWithPath(
                    _moduleId,
                    159,
                    "Controls whether a suggestion is selected when the widget shows. Note that this only applies to automatically triggered suggestions (`#editor.quickSuggestions#` and `#editor.suggestOnTriggerCharacters#`) and that a suggestion is always selected when explicitly invoked, e.g via `Ctrl+Space`."
                ))
            },
            'editor.suggest.snippetsPreventQuickSuggestions': {
                type: 'boolean',
                default: defaults.snippetsPreventQuickSuggestions,
                description: ( localizeWithPath(
                    _moduleId,
                    160,
                    "Controls whether an active snippet prevents quick suggestions."
                ))
            },
            'editor.suggest.showIcons': {
                type: 'boolean',
                default: defaults.showIcons,
                description: ( localizeWithPath(_moduleId, 161, "Controls whether to show or hide icons in suggestions."))
            },
            'editor.suggest.showStatusBar': {
                type: 'boolean',
                default: defaults.showStatusBar,
                description: ( localizeWithPath(
                    _moduleId,
                    162,
                    "Controls the visibility of the status bar at the bottom of the suggest widget."
                ))
            },
            'editor.suggest.preview': {
                type: 'boolean',
                default: defaults.preview,
                description: ( localizeWithPath(
                    _moduleId,
                    163,
                    "Controls whether to preview the suggestion outcome in the editor."
                ))
            },
            'editor.suggest.showInlineDetails': {
                type: 'boolean',
                default: defaults.showInlineDetails,
                description: ( localizeWithPath(
                    _moduleId,
                    164,
                    "Controls whether suggest details show inline with the label or only in the details widget."
                ))
            },
            'editor.suggest.maxVisibleSuggestions': {
                type: 'number',
                deprecationMessage: ( localizeWithPath(
                    _moduleId,
                    165,
                    "This setting is deprecated. The suggest widget can now be resized."
                )),
            },
            'editor.suggest.filteredTypes': {
                type: 'object',
                deprecationMessage: ( localizeWithPath(
                    _moduleId,
                    166,
                    "This setting is deprecated, please use separate settings like 'editor.suggest.showKeywords' or 'editor.suggest.showSnippets' instead."
                ))
            },
            'editor.suggest.showMethods': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 167, "When enabled IntelliSense shows `method`-suggestions."))
            },
            'editor.suggest.showFunctions': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 168, "When enabled IntelliSense shows `function`-suggestions."))
            },
            'editor.suggest.showConstructors': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(
                    _moduleId,
                    169,
                    "When enabled IntelliSense shows `constructor`-suggestions."
                ))
            },
            'editor.suggest.showDeprecated': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(
                    _moduleId,
                    170,
                    "When enabled IntelliSense shows `deprecated`-suggestions."
                ))
            },
            'editor.suggest.matchOnWordStartOnly': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(
                    _moduleId,
                    171,
                    "When enabled IntelliSense filtering requires that the first character matches on a word start. For example, `c` on `Console` or `WebContext` but _not_ on `description`. When disabled IntelliSense will show more results but still sorts them by match quality."
                ))
            },
            'editor.suggest.showFields': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 172, "When enabled IntelliSense shows `field`-suggestions."))
            },
            'editor.suggest.showVariables': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 173, "When enabled IntelliSense shows `variable`-suggestions."))
            },
            'editor.suggest.showClasses': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 174, "When enabled IntelliSense shows `class`-suggestions."))
            },
            'editor.suggest.showStructs': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 175, "When enabled IntelliSense shows `struct`-suggestions."))
            },
            'editor.suggest.showInterfaces': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 176, "When enabled IntelliSense shows `interface`-suggestions."))
            },
            'editor.suggest.showModules': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 177, "When enabled IntelliSense shows `module`-suggestions."))
            },
            'editor.suggest.showProperties': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 178, "When enabled IntelliSense shows `property`-suggestions."))
            },
            'editor.suggest.showEvents': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 179, "When enabled IntelliSense shows `event`-suggestions."))
            },
            'editor.suggest.showOperators': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 180, "When enabled IntelliSense shows `operator`-suggestions."))
            },
            'editor.suggest.showUnits': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 181, "When enabled IntelliSense shows `unit`-suggestions."))
            },
            'editor.suggest.showValues': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 182, "When enabled IntelliSense shows `value`-suggestions."))
            },
            'editor.suggest.showConstants': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 183, "When enabled IntelliSense shows `constant`-suggestions."))
            },
            'editor.suggest.showEnums': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 184, "When enabled IntelliSense shows `enum`-suggestions."))
            },
            'editor.suggest.showEnumMembers': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(
                    _moduleId,
                    185,
                    "When enabled IntelliSense shows `enumMember`-suggestions."
                ))
            },
            'editor.suggest.showKeywords': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 186, "When enabled IntelliSense shows `keyword`-suggestions."))
            },
            'editor.suggest.showWords': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 187, "When enabled IntelliSense shows `text`-suggestions."))
            },
            'editor.suggest.showColors': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 188, "When enabled IntelliSense shows `color`-suggestions."))
            },
            'editor.suggest.showFiles': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 189, "When enabled IntelliSense shows `file`-suggestions."))
            },
            'editor.suggest.showReferences': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 190, "When enabled IntelliSense shows `reference`-suggestions."))
            },
            'editor.suggest.showCustomcolors': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(
                    _moduleId,
                    191,
                    "When enabled IntelliSense shows `customcolor`-suggestions."
                ))
            },
            'editor.suggest.showFolders': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 192, "When enabled IntelliSense shows `folder`-suggestions."))
            },
            'editor.suggest.showTypeParameters': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(
                    _moduleId,
                    193,
                    "When enabled IntelliSense shows `typeParameter`-suggestions."
                ))
            },
            'editor.suggest.showSnippets': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 194, "When enabled IntelliSense shows `snippet`-suggestions."))
            },
            'editor.suggest.showUsers': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 195, "When enabled IntelliSense shows `user`-suggestions."))
            },
            'editor.suggest.showIssues': {
                type: 'boolean',
                default: true,
                markdownDescription: ( localizeWithPath(_moduleId, 196, "When enabled IntelliSense shows `issues`-suggestions."))
            }
        });
    }
    validate(_input) {
        if (!_input || typeof _input !== 'object') {
            return this.defaultValue;
        }
        const input = _input;
        return {
            insertMode: stringSet(input.insertMode, this.defaultValue.insertMode, ['insert', 'replace']),
            filterGraceful: boolean(input.filterGraceful, this.defaultValue.filterGraceful),
            snippetsPreventQuickSuggestions: boolean(input.snippetsPreventQuickSuggestions, this.defaultValue.filterGraceful),
            localityBonus: boolean(input.localityBonus, this.defaultValue.localityBonus),
            shareSuggestSelections: boolean(input.shareSuggestSelections, this.defaultValue.shareSuggestSelections),
            selectionMode: stringSet(input.selectionMode, this.defaultValue.selectionMode, ['always', 'never', 'whenQuickSuggestion', 'whenTriggerCharacter']),
            showIcons: boolean(input.showIcons, this.defaultValue.showIcons),
            showStatusBar: boolean(input.showStatusBar, this.defaultValue.showStatusBar),
            preview: boolean(input.preview, this.defaultValue.preview),
            previewMode: stringSet(input.previewMode, this.defaultValue.previewMode, ['prefix', 'subword', 'subwordSmart']),
            showInlineDetails: boolean(input.showInlineDetails, this.defaultValue.showInlineDetails),
            showMethods: boolean(input.showMethods, this.defaultValue.showMethods),
            showFunctions: boolean(input.showFunctions, this.defaultValue.showFunctions),
            showConstructors: boolean(input.showConstructors, this.defaultValue.showConstructors),
            showDeprecated: boolean(input.showDeprecated, this.defaultValue.showDeprecated),
            matchOnWordStartOnly: boolean(input.matchOnWordStartOnly, this.defaultValue.matchOnWordStartOnly),
            showFields: boolean(input.showFields, this.defaultValue.showFields),
            showVariables: boolean(input.showVariables, this.defaultValue.showVariables),
            showClasses: boolean(input.showClasses, this.defaultValue.showClasses),
            showStructs: boolean(input.showStructs, this.defaultValue.showStructs),
            showInterfaces: boolean(input.showInterfaces, this.defaultValue.showInterfaces),
            showModules: boolean(input.showModules, this.defaultValue.showModules),
            showProperties: boolean(input.showProperties, this.defaultValue.showProperties),
            showEvents: boolean(input.showEvents, this.defaultValue.showEvents),
            showOperators: boolean(input.showOperators, this.defaultValue.showOperators),
            showUnits: boolean(input.showUnits, this.defaultValue.showUnits),
            showValues: boolean(input.showValues, this.defaultValue.showValues),
            showConstants: boolean(input.showConstants, this.defaultValue.showConstants),
            showEnums: boolean(input.showEnums, this.defaultValue.showEnums),
            showEnumMembers: boolean(input.showEnumMembers, this.defaultValue.showEnumMembers),
            showKeywords: boolean(input.showKeywords, this.defaultValue.showKeywords),
            showWords: boolean(input.showWords, this.defaultValue.showWords),
            showColors: boolean(input.showColors, this.defaultValue.showColors),
            showFiles: boolean(input.showFiles, this.defaultValue.showFiles),
            showReferences: boolean(input.showReferences, this.defaultValue.showReferences),
            showFolders: boolean(input.showFolders, this.defaultValue.showFolders),
            showTypeParameters: boolean(input.showTypeParameters, this.defaultValue.showTypeParameters),
            showSnippets: boolean(input.showSnippets, this.defaultValue.showSnippets),
            showUsers: boolean(input.showUsers, this.defaultValue.showUsers),
            showIssues: boolean(input.showIssues, this.defaultValue.showIssues),
        };
    }
}
class SmartSelect extends BaseEditorOption {
    constructor() {
        super(113 , 'smartSelect', {
            selectLeadingAndTrailingWhitespace: true,
            selectSubwords: true,
        }, {
            'editor.smartSelect.selectLeadingAndTrailingWhitespace': {
                description: ( localizeWithPath(
                    _moduleId,
                    197,
                    "Whether leading and trailing whitespace should always be selected."
                )),
                default: true,
                type: 'boolean'
            },
            'editor.smartSelect.selectSubwords': {
                description: ( localizeWithPath(
                    _moduleId,
                    198,
                    "Whether subwords (like 'foo' in 'fooBar' or 'foo_bar') should be selected."
                )),
                default: true,
                type: 'boolean'
            }
        });
    }
    validate(input) {
        if (!input || typeof input !== 'object') {
            return this.defaultValue;
        }
        return {
            selectLeadingAndTrailingWhitespace: boolean(input.selectLeadingAndTrailingWhitespace, this.defaultValue.selectLeadingAndTrailingWhitespace),
            selectSubwords: boolean(input.selectSubwords, this.defaultValue.selectSubwords),
        };
    }
}
class WordSegmenterLocales extends BaseEditorOption {
    constructor() {
        const defaults = [];
        super(130 , 'wordSegmenterLocales', defaults, {
            anyOf: [
                {
                    description: ( localizeWithPath(
                        _moduleId,
                        199,
                        "Locales to be used for word segmentation when doing word related navigations or operations. Specify the BCP 47 language tag of the word you wish to recognize (e.g., ja, zh-CN, zh-Hant-TW, etc.)."
                    )),
                    type: 'string',
                }, {
                    description: ( localizeWithPath(
                        _moduleId,
                        199,
                        "Locales to be used for word segmentation when doing word related navigations or operations. Specify the BCP 47 language tag of the word you wish to recognize (e.g., ja, zh-CN, zh-Hant-TW, etc.)."
                    )),
                    type: 'array',
                    items: {
                        type: 'string'
                    }
                }
            ]
        });
    }
    validate(input) {
        if (typeof input === 'string') {
            input = [input];
        }
        if (Array.isArray(input)) {
            const validLocales = [];
            for (const locale of input) {
                if (typeof locale === 'string') {
                    try {
                        if (Intl.Segmenter.supportedLocalesOf(locale).length > 0) {
                            validLocales.push(locale);
                        }
                    }
                    catch {
                    }
                }
            }
            return validLocales;
        }
        return this.defaultValue;
    }
}
class WrappingIndentOption extends BaseEditorOption {
    constructor() {
        super(138 , 'wrappingIndent', 1 , {
            'editor.wrappingIndent': {
                type: 'string',
                enum: ['none', 'same', 'indent', 'deepIndent'],
                enumDescriptions: [
                    ( localizeWithPath(_moduleId, 200, "No indentation. Wrapped lines begin at column 1.")),
                    ( localizeWithPath(_moduleId, 201, "Wrapped lines get the same indentation as the parent.")),
                    ( localizeWithPath(_moduleId, 202, "Wrapped lines get +1 indentation toward the parent.")),
                    ( localizeWithPath(_moduleId, 203, "Wrapped lines get +2 indentation toward the parent.")),
                ],
                description: ( localizeWithPath(_moduleId, 204, "Controls the indentation of wrapped lines.")),
                default: 'same'
            }
        });
    }
    validate(input) {
        switch (input) {
            case 'none': return 0 ;
            case 'same': return 1 ;
            case 'indent': return 2 ;
            case 'deepIndent': return 3 ;
        }
        return 1 ;
    }
    compute(env, options, value) {
        const accessibilitySupport = options.get(2 );
        if (accessibilitySupport === 2 ) {
            return 0 ;
        }
        return value;
    }
}
class EditorWrappingInfoComputer extends ComputedEditorOption {
    constructor() {
        super(146 );
    }
    compute(env, options, _) {
        const layoutInfo = options.get(145 );
        return {
            isDominatedByLongLines: env.isDominatedByLongLines,
            isWordWrapMinified: layoutInfo.isWordWrapMinified,
            isViewportWrapping: layoutInfo.isViewportWrapping,
            wrappingColumn: layoutInfo.wrappingColumn,
        };
    }
}
class EditorDropIntoEditor extends BaseEditorOption {
    constructor() {
        const defaults = { enabled: true, showDropSelector: 'afterDrop' };
        super(36 , 'dropIntoEditor', defaults, {
            'editor.dropIntoEditor.enabled': {
                type: 'boolean',
                default: defaults.enabled,
                markdownDescription: ( localizeWithPath(
                    _moduleId,
                    205,
                    "Controls whether you can drag and drop a file into a text editor by holding down the `Shift` key (instead of opening the file in an editor)."
                )),
            },
            'editor.dropIntoEditor.showDropSelector': {
                type: 'string',
                markdownDescription: ( localizeWithPath(
                    _moduleId,
                    206,
                    "Controls if a widget is shown when dropping files into the editor. This widget lets you control how the file is dropped."
                )),
                enum: [
                    'afterDrop',
                    'never'
                ],
                enumDescriptions: [
                    ( localizeWithPath(
                        _moduleId,
                        207,
                        "Show the drop selector widget after a file is dropped into the editor."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        208,
                        "Never show the drop selector widget. Instead the default drop provider is always used."
                    )),
                ],
                default: 'afterDrop',
            },
        });
    }
    validate(_input) {
        if (!_input || typeof _input !== 'object') {
            return this.defaultValue;
        }
        const input = _input;
        return {
            enabled: boolean(input.enabled, this.defaultValue.enabled),
            showDropSelector: stringSet(input.showDropSelector, this.defaultValue.showDropSelector, ['afterDrop', 'never']),
        };
    }
}
class EditorPasteAs extends BaseEditorOption {
    constructor() {
        const defaults = { enabled: true, showPasteSelector: 'afterPaste' };
        super(85 , 'pasteAs', defaults, {
            'editor.pasteAs.enabled': {
                type: 'boolean',
                default: defaults.enabled,
                markdownDescription: ( localizeWithPath(
                    _moduleId,
                    209,
                    "Controls whether you can paste content in different ways."
                )),
            },
            'editor.pasteAs.showPasteSelector': {
                type: 'string',
                markdownDescription: ( localizeWithPath(
                    _moduleId,
                    210,
                    "Controls if a widget is shown when pasting content in to the editor. This widget lets you control how the file is pasted."
                )),
                enum: [
                    'afterPaste',
                    'never'
                ],
                enumDescriptions: [
                    ( localizeWithPath(
                        _moduleId,
                        211,
                        "Show the paste selector widget after content is pasted into the editor."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        212,
                        "Never show the paste selector widget. Instead the default pasting behavior is always used."
                    )),
                ],
                default: 'afterPaste',
            },
        });
    }
    validate(_input) {
        if (!_input || typeof _input !== 'object') {
            return this.defaultValue;
        }
        const input = _input;
        return {
            enabled: boolean(input.enabled, this.defaultValue.enabled),
            showPasteSelector: stringSet(input.showPasteSelector, this.defaultValue.showPasteSelector, ['afterPaste', 'never']),
        };
    }
}
const DEFAULT_WINDOWS_FONT_FAMILY = 'Consolas, \'Courier New\', monospace';
const DEFAULT_MAC_FONT_FAMILY = 'Menlo, Monaco, \'Courier New\', monospace';
const DEFAULT_LINUX_FONT_FAMILY = '\'Droid Sans Mono\', \'monospace\', monospace';
const EDITOR_FONT_DEFAULTS = {
    fontFamily: (isMacintosh ? DEFAULT_MAC_FONT_FAMILY : (isLinux ? DEFAULT_LINUX_FONT_FAMILY : DEFAULT_WINDOWS_FONT_FAMILY)),
    fontWeight: 'normal',
    fontSize: (isMacintosh ? 12 : 14),
    lineHeight: 0,
    letterSpacing: 0,
};
const editorOptionsRegistry = [];
function register(option) {
    editorOptionsRegistry[option.id] = option;
    return option;
}
const EditorOptions = {
    acceptSuggestionOnCommitCharacter: register(( (new EditorBooleanOption(
        0 ,
        'acceptSuggestionOnCommitCharacter',
        true,
        { markdownDescription: ( localizeWithPath(
            _moduleId,
            213,
            "Controls whether suggestions should be accepted on commit characters. For example, in JavaScript, the semi-colon (`;`) can be a commit character that accepts a suggestion and types that character."
        )) }
    )))),
    acceptSuggestionOnEnter: register(( (new EditorStringEnumOption(
        1 ,
        'acceptSuggestionOnEnter',
        'on',
        ['on', 'smart', 'off'],
        {
            markdownEnumDescriptions: [
                '',
                ( localizeWithPath(
                    _moduleId,
                    214,
                    "Only accept a suggestion with `Enter` when it makes a textual change."
                )),
                ''
            ],
            markdownDescription: ( localizeWithPath(
                _moduleId,
                215,
                "Controls whether suggestions should be accepted on `Enter`, in addition to `Tab`. Helps to avoid ambiguity between inserting new lines or accepting suggestions."
            ))
        }
    )))),
    accessibilitySupport: register(( (new EditorAccessibilitySupport()))),
    accessibilityPageSize: register(( (new EditorIntOption(
        3 ,
        'accessibilityPageSize',
        10,
        1,
        1073741824 ,
        {
            description: ( localizeWithPath(
                _moduleId,
                216,
                "Controls the number of lines in the editor that can be read out by a screen reader at once. When we detect a screen reader we automatically set the default to be 500. Warning: this has a performance implication for numbers larger than the default."
            )),
            tags: ['accessibility']
        }
    )))),
    ariaLabel: register(( (new EditorStringOption(4 , 'ariaLabel', ( localizeWithPath(_moduleId, 217, "Editor content")))))),
    ariaRequired: register(( (new EditorBooleanOption(5 , 'ariaRequired', false, undefined)))),
    screenReaderAnnounceInlineSuggestion: register(( (new EditorBooleanOption(
        8 ,
        'screenReaderAnnounceInlineSuggestion',
        true,
        {
            description: ( localizeWithPath(
                _moduleId,
                218,
                "Control whether inline suggestions are announced by a screen reader."
            )),
            tags: ['accessibility']
        }
    )))),
    autoClosingBrackets: register(( (new EditorStringEnumOption(
        6 ,
        'autoClosingBrackets',
        'languageDefined',
        ['always', 'languageDefined', 'beforeWhitespace', 'never'],
        {
            enumDescriptions: [
                '',
                ( localizeWithPath(
                    _moduleId,
                    219,
                    "Use language configurations to determine when to autoclose brackets."
                )),
                ( localizeWithPath(
                    _moduleId,
                    220,
                    "Autoclose brackets only when the cursor is to the left of whitespace."
                )),
                '',
            ],
            description: ( localizeWithPath(
                _moduleId,
                221,
                "Controls whether the editor should automatically close brackets after the user adds an opening bracket."
            ))
        }
    )))),
    autoClosingComments: register(( (new EditorStringEnumOption(
        7 ,
        'autoClosingComments',
        'languageDefined',
        ['always', 'languageDefined', 'beforeWhitespace', 'never'],
        {
            enumDescriptions: [
                '',
                ( localizeWithPath(
                    _moduleId,
                    222,
                    "Use language configurations to determine when to autoclose comments."
                )),
                ( localizeWithPath(
                    _moduleId,
                    223,
                    "Autoclose comments only when the cursor is to the left of whitespace."
                )),
                '',
            ],
            description: ( localizeWithPath(
                _moduleId,
                224,
                "Controls whether the editor should automatically close comments after the user adds an opening comment."
            ))
        }
    )))),
    autoClosingDelete: register(( (new EditorStringEnumOption(
        9 ,
        'autoClosingDelete',
        'auto',
        ['always', 'auto', 'never'],
        {
            enumDescriptions: [
                '',
                ( localizeWithPath(
                    _moduleId,
                    225,
                    "Remove adjacent closing quotes or brackets only if they were automatically inserted."
                )),
                '',
            ],
            description: ( localizeWithPath(
                _moduleId,
                226,
                "Controls whether the editor should remove adjacent closing quotes or brackets when deleting."
            ))
        }
    )))),
    autoClosingOvertype: register(( (new EditorStringEnumOption(
        10 ,
        'autoClosingOvertype',
        'auto',
        ['always', 'auto', 'never'],
        {
            enumDescriptions: [
                '',
                ( localizeWithPath(
                    _moduleId,
                    227,
                    "Type over closing quotes or brackets only if they were automatically inserted."
                )),
                '',
            ],
            description: ( localizeWithPath(
                _moduleId,
                228,
                "Controls whether the editor should type over closing quotes or brackets."
            ))
        }
    )))),
    autoClosingQuotes: register(( (new EditorStringEnumOption(
        11 ,
        'autoClosingQuotes',
        'languageDefined',
        ['always', 'languageDefined', 'beforeWhitespace', 'never'],
        {
            enumDescriptions: [
                '',
                ( localizeWithPath(
                    _moduleId,
                    229,
                    "Use language configurations to determine when to autoclose quotes."
                )),
                ( localizeWithPath(
                    _moduleId,
                    230,
                    "Autoclose quotes only when the cursor is to the left of whitespace."
                )),
                '',
            ],
            description: ( localizeWithPath(
                _moduleId,
                231,
                "Controls whether the editor should automatically close quotes after the user adds an opening quote."
            ))
        }
    )))),
    autoIndent: register(( (new EditorEnumOption(
        12 ,
        'autoIndent',
        4 ,
        'full',
        ['none', 'keep', 'brackets', 'advanced', 'full'],
        _autoIndentFromString,
        {
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 232, "The editor will not insert indentation automatically.")),
                ( localizeWithPath(_moduleId, 233, "The editor will keep the current line's indentation.")),
                ( localizeWithPath(
                    _moduleId,
                    234,
                    "The editor will keep the current line's indentation and honor language defined brackets."
                )),
                ( localizeWithPath(
                    _moduleId,
                    235,
                    "The editor will keep the current line's indentation, honor language defined brackets and invoke special onEnterRules defined by languages."
                )),
                ( localizeWithPath(
                    _moduleId,
                    236,
                    "The editor will keep the current line's indentation, honor language defined brackets, invoke special onEnterRules defined by languages, and honor indentationRules defined by languages."
                )),
            ],
            description: ( localizeWithPath(
                _moduleId,
                237,
                "Controls whether the editor should automatically adjust the indentation when users type, paste, move or indent lines."
            ))
        }
    )))),
    automaticLayout: register(( (new EditorBooleanOption(13 , 'automaticLayout', false)))),
    autoSurround: register(( (new EditorStringEnumOption(
        14 ,
        'autoSurround',
        'languageDefined',
        ['languageDefined', 'quotes', 'brackets', 'never'],
        {
            enumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    238,
                    "Use language configurations to determine when to automatically surround selections."
                )),
                ( localizeWithPath(_moduleId, 239, "Surround with quotes but not brackets.")),
                ( localizeWithPath(_moduleId, 240, "Surround with brackets but not quotes.")),
                ''
            ],
            description: ( localizeWithPath(
                _moduleId,
                241,
                "Controls whether the editor should automatically surround selections when typing quotes or brackets."
            ))
        }
    )))),
    bracketPairColorization: register(( (new BracketPairColorization()))),
    bracketPairGuides: register(( (new GuideOptions()))),
    stickyTabStops: register(( (new EditorBooleanOption(
        116 ,
        'stickyTabStops',
        false,
        { description: ( localizeWithPath(
            _moduleId,
            242,
            "Emulate selection behavior of tab characters when using spaces for indentation. Selection will stick to tab stops."
        )) }
    )))),
    codeLens: register(( (new EditorBooleanOption(
        17 ,
        'codeLens',
        true,
        { description: ( localizeWithPath(_moduleId, 243, "Controls whether the editor shows CodeLens.")) }
    )))),
    codeLensFontFamily: register(( (new EditorStringOption(
        18 ,
        'codeLensFontFamily',
        '',
        { description: ( localizeWithPath(_moduleId, 244, "Controls the font family for CodeLens.")) }
    )))),
    codeLensFontSize: register(( (new EditorIntOption(19 , 'codeLensFontSize', 0, 0, 100, {
        type: 'number',
        default: 0,
        minimum: 0,
        maximum: 100,
        markdownDescription: ( localizeWithPath(
            _moduleId,
            245,
            "Controls the font size in pixels for CodeLens. When set to 0, 90% of `#editor.fontSize#` is used."
        ))
    })))),
    colorDecorators: register(( (new EditorBooleanOption(
        20 ,
        'colorDecorators',
        true,
        { description: ( localizeWithPath(
            _moduleId,
            246,
            "Controls whether the editor should render the inline color decorators and color picker."
        )) }
    )))),
    colorDecoratorActivatedOn: register(( (new EditorStringEnumOption(
        148 ,
        'colorDecoratorsActivatedOn',
        'clickAndHover',
        ['clickAndHover', 'hover', 'click'],
        {
            enumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    247,
                    "Make the color picker appear both on click and hover of the color decorator"
                )),
                ( localizeWithPath(
                    _moduleId,
                    248,
                    "Make the color picker appear on hover of the color decorator"
                )),
                ( localizeWithPath(
                    _moduleId,
                    249,
                    "Make the color picker appear on click of the color decorator"
                ))
            ],
            description: ( localizeWithPath(
                _moduleId,
                250,
                "Controls the condition to make a color picker appear from a color decorator"
            ))
        }
    )))),
    colorDecoratorsLimit: register(( (new EditorIntOption(
        21 ,
        'colorDecoratorsLimit',
        500,
        1,
        1000000,
        {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                251,
                "Controls the max number of color decorators that can be rendered in an editor at once."
            ))
        }
    )))),
    columnSelection: register(( (new EditorBooleanOption(
        22 ,
        'columnSelection',
        false,
        { description: ( localizeWithPath(
            _moduleId,
            252,
            "Enable that the selection with the mouse and keys is doing column selection."
        )) }
    )))),
    comments: register(( (new EditorComments()))),
    contextmenu: register(( (new EditorBooleanOption(24 , 'contextmenu', true)))),
    copyWithSyntaxHighlighting: register(( (new EditorBooleanOption(
        25 ,
        'copyWithSyntaxHighlighting',
        true,
        { description: ( localizeWithPath(
            _moduleId,
            253,
            "Controls whether syntax highlighting should be copied into the clipboard."
        )) }
    )))),
    cursorBlinking: register(( (new EditorEnumOption(
        26 ,
        'cursorBlinking',
        1 ,
        'blink',
        ['blink', 'smooth', 'phase', 'expand', 'solid'],
        _cursorBlinkingStyleFromString,
        { description: ( localizeWithPath(_moduleId, 254, "Control the cursor animation style.")) }
    )))),
    cursorSmoothCaretAnimation: register(( (new EditorStringEnumOption(
        27 ,
        'cursorSmoothCaretAnimation',
        'off',
        ['off', 'explicit', 'on'],
        {
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 255, "Smooth caret animation is disabled.")),
                ( localizeWithPath(
                    _moduleId,
                    256,
                    "Smooth caret animation is enabled only when the user moves the cursor with an explicit gesture."
                )),
                ( localizeWithPath(_moduleId, 257, "Smooth caret animation is always enabled."))
            ],
            description: ( localizeWithPath(
                _moduleId,
                258,
                "Controls whether the smooth caret animation should be enabled."
            ))
        }
    )))),
    cursorStyle: register(( (new EditorEnumOption(
        28 ,
        'cursorStyle',
        TextEditorCursorStyle.Line,
        'line',
        ['line', 'block', 'underline', 'line-thin', 'block-outline', 'underline-thin'],
        _cursorStyleFromString,
        { description: ( localizeWithPath(_moduleId, 259, "Controls the cursor style.")) }
    )))),
    cursorSurroundingLines: register(( (new EditorIntOption(
        29 ,
        'cursorSurroundingLines',
        0,
        0,
        1073741824 ,
        { description: ( localizeWithPath(
            _moduleId,
            260,
            "Controls the minimal number of visible leading lines (minimum 0) and trailing lines (minimum 1) surrounding the cursor. Known as 'scrollOff' or 'scrollOffset' in some other editors."
        )) }
    )))),
    cursorSurroundingLinesStyle: register(( (new EditorStringEnumOption(
        30 ,
        'cursorSurroundingLinesStyle',
        'default',
        ['default', 'all'],
        {
            enumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    261,
                    "`cursorSurroundingLines` is enforced only when triggered via the keyboard or API."
                )),
                ( localizeWithPath(_moduleId, 262, "`cursorSurroundingLines` is enforced always."))
            ],
            markdownDescription: ( localizeWithPath(
                _moduleId,
                263,
                "Controls when `#editor.cursorSurroundingLines#` should be enforced."
            ))
        }
    )))),
    cursorWidth: register(( (new EditorIntOption(
        31 ,
        'cursorWidth',
        0,
        0,
        1073741824 ,
        { markdownDescription: ( localizeWithPath(
            _moduleId,
            264,
            "Controls the width of the cursor when `#editor.cursorStyle#` is set to `line`."
        )) }
    )))),
    disableLayerHinting: register(( (new EditorBooleanOption(32 , 'disableLayerHinting', false)))),
    disableMonospaceOptimizations: register(( (new EditorBooleanOption(
        33 ,
        'disableMonospaceOptimizations',
        false
    )))),
    domReadOnly: register(( (new EditorBooleanOption(34 , 'domReadOnly', false)))),
    dragAndDrop: register(( (new EditorBooleanOption(
        35 ,
        'dragAndDrop',
        true,
        { description: ( localizeWithPath(
            _moduleId,
            265,
            "Controls whether the editor should allow moving selections via drag and drop."
        )) }
    )))),
    emptySelectionClipboard: register(( (new EditorEmptySelectionClipboard()))),
    dropIntoEditor: register(( (new EditorDropIntoEditor()))),
    stickyScroll: register(( (new EditorStickyScroll()))),
    experimentalWhitespaceRendering: register(( (new EditorStringEnumOption(
        38 ,
        'experimentalWhitespaceRendering',
        'svg',
        ['svg', 'font', 'off'],
        {
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 266, "Use a new rendering method with svgs.")),
                ( localizeWithPath(_moduleId, 267, "Use a new rendering method with font characters.")),
                ( localizeWithPath(_moduleId, 268, "Use the stable rendering method.")),
            ],
            description: ( localizeWithPath(
                _moduleId,
                269,
                "Controls whether whitespace is rendered with a new, experimental method."
            ))
        }
    )))),
    extraEditorClassName: register(( (new EditorStringOption(39 , 'extraEditorClassName', '')))),
    fastScrollSensitivity: register(( (new EditorFloatOption(
        40 ,
        'fastScrollSensitivity',
        5,
        x => (x <= 0 ? 5 : x),
        { markdownDescription: ( localizeWithPath(_moduleId, 270, "Scrolling speed multiplier when pressing `Alt`.")) }
    )))),
    find: register(( (new EditorFind()))),
    fixedOverflowWidgets: register(( (new EditorBooleanOption(42 , 'fixedOverflowWidgets', false)))),
    folding: register(( (new EditorBooleanOption(
        43 ,
        'folding',
        true,
        { description: ( localizeWithPath(_moduleId, 271, "Controls whether the editor has code folding enabled.")) }
    )))),
    foldingStrategy: register(( (new EditorStringEnumOption(
        44 ,
        'foldingStrategy',
        'auto',
        ['auto', 'indentation'],
        {
            enumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    272,
                    "Use a language-specific folding strategy if available, else the indentation-based one."
                )),
                ( localizeWithPath(_moduleId, 273, "Use the indentation-based folding strategy.")),
            ],
            description: ( localizeWithPath(_moduleId, 274, "Controls the strategy for computing folding ranges."))
        }
    )))),
    foldingHighlight: register(( (new EditorBooleanOption(
        45 ,
        'foldingHighlight',
        true,
        { description: ( localizeWithPath(
            _moduleId,
            275,
            "Controls whether the editor should highlight folded ranges."
        )) }
    )))),
    foldingImportsByDefault: register(( (new EditorBooleanOption(
        46 ,
        'foldingImportsByDefault',
        false,
        { description: ( localizeWithPath(
            _moduleId,
            276,
            "Controls whether the editor automatically collapses import ranges."
        )) }
    )))),
    foldingMaximumRegions: register(( (new EditorIntOption(
        47 ,
        'foldingMaximumRegions',
        5000,
        10,
        65000,
        { description: ( localizeWithPath(
            _moduleId,
            277,
            "The maximum number of foldable regions. Increasing this value may result in the editor becoming less responsive when the current source has a large number of foldable regions."
        )) }
    )))),
    unfoldOnClickAfterEndOfLine: register(( (new EditorBooleanOption(
        48 ,
        'unfoldOnClickAfterEndOfLine',
        false,
        { description: ( localizeWithPath(
            _moduleId,
            278,
            "Controls whether clicking on the empty content after a folded line will unfold the line."
        )) }
    )))),
    fontFamily: register(( (new EditorStringOption(
        49 ,
        'fontFamily',
        EDITOR_FONT_DEFAULTS.fontFamily,
        { description: ( localizeWithPath(_moduleId, 279, "Controls the font family.")) }
    )))),
    fontInfo: register(( (new EditorFontInfo()))),
    fontLigatures2: register(( (new EditorFontLigatures()))),
    fontSize: register(( (new EditorFontSize()))),
    fontWeight: register(( (new EditorFontWeight()))),
    fontVariations: register(( (new EditorFontVariations()))),
    formatOnPaste: register(( (new EditorBooleanOption(
        55 ,
        'formatOnPaste',
        false,
        { description: ( localizeWithPath(
            _moduleId,
            280,
            "Controls whether the editor should automatically format the pasted content. A formatter must be available and the formatter should be able to format a range in a document."
        )) }
    )))),
    formatOnType: register(( (new EditorBooleanOption(
        56 ,
        'formatOnType',
        false,
        { description: ( localizeWithPath(
            _moduleId,
            281,
            "Controls whether the editor should automatically format the line after typing."
        )) }
    )))),
    glyphMargin: register(( (new EditorBooleanOption(
        57 ,
        'glyphMargin',
        true,
        { description: ( localizeWithPath(
            _moduleId,
            282,
            "Controls whether the editor should render the vertical glyph margin. Glyph margin is mostly used for debugging."
        )) }
    )))),
    gotoLocation: register(( (new EditorGoToLocation()))),
    hideCursorInOverviewRuler: register(( (new EditorBooleanOption(
        59 ,
        'hideCursorInOverviewRuler',
        false,
        { description: ( localizeWithPath(
            _moduleId,
            283,
            "Controls whether the cursor should be hidden in the overview ruler."
        )) }
    )))),
    hover: register(( (new EditorHover()))),
    inDiffEditor: register(( (new EditorBooleanOption(61 , 'inDiffEditor', false)))),
    letterSpacing: register(( (new EditorFloatOption(
        64 ,
        'letterSpacing',
        EDITOR_FONT_DEFAULTS.letterSpacing,
        x => EditorFloatOption.clamp(x, -5, 20),
        { description: ( localizeWithPath(_moduleId, 284, "Controls the letter spacing in pixels.")) }
    )))),
    lightbulb: register(( (new EditorLightbulb()))),
    lineDecorationsWidth: register(( (new EditorLineDecorationsWidth()))),
    lineHeight: register(( (new EditorLineHeight()))),
    lineNumbers: register(( (new EditorRenderLineNumbersOption()))),
    lineNumbersMinChars: register(( (new EditorIntOption(
        69 ,
        'lineNumbersMinChars',
        5,
        1,
        300
    )))),
    linkedEditing: register(( (new EditorBooleanOption(
        70 ,
        'linkedEditing',
        false,
        { description: ( localizeWithPath(
            _moduleId,
            285,
            "Controls whether the editor has linked editing enabled. Depending on the language, related symbols such as HTML tags, are updated while editing."
        )) }
    )))),
    links: register(( (new EditorBooleanOption(71 , 'links', true, { description: ( localizeWithPath(
        _moduleId,
        286,
        "Controls whether the editor should detect links and make them clickable."
    )) })))),
    matchBrackets: register(( (new EditorStringEnumOption(
        72 ,
        'matchBrackets',
        'always',
        ['always', 'near', 'never'],
        { description: ( localizeWithPath(_moduleId, 287, "Highlight matching brackets.")) }
    )))),
    minimap: register(( (new EditorMinimap()))),
    mouseStyle: register(( (new EditorStringEnumOption(
        74 ,
        'mouseStyle',
        'text',
        ['text', 'default', 'copy']
    )))),
    mouseWheelScrollSensitivity: register(( (new EditorFloatOption(
        75 ,
        'mouseWheelScrollSensitivity',
        1,
        x => (x === 0 ? 1 : x),
        { markdownDescription: ( localizeWithPath(
            _moduleId,
            288,
            "A multiplier to be used on the `deltaX` and `deltaY` of mouse wheel scroll events."
        )) }
    )))),
    mouseWheelZoom: register(( (new EditorBooleanOption(76 , 'mouseWheelZoom', false, {
        markdownDescription: isMacintosh
            ? ( localizeWithPath(
            _moduleId,
            289,
            "Zoom the font of the editor when using mouse wheel and holding `Cmd`."
        ))
            : ( localizeWithPath(
            _moduleId,
            290,
            "Zoom the font of the editor when using mouse wheel and holding `Ctrl`."
        ))
    })))),
    multiCursorMergeOverlapping: register(( (new EditorBooleanOption(
        77 ,
        'multiCursorMergeOverlapping',
        true,
        { description: ( localizeWithPath(_moduleId, 291, "Merge multiple cursors when they are overlapping.")) }
    )))),
    multiCursorModifier: register(( (new EditorEnumOption(
        78 ,
        'multiCursorModifier',
        'altKey',
        'alt',
        ['ctrlCmd', 'alt'],
        _multiCursorModifierFromString,
        {
            markdownEnumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    292,
                    "Maps to `Control` on Windows and Linux and to `Command` on macOS."
                )),
                ( localizeWithPath(
                    _moduleId,
                    293,
                    "Maps to `Alt` on Windows and Linux and to `Option` on macOS."
                ))
            ],
            markdownDescription: ( localizeWithPath(
                _moduleId,
                294,
                "The modifier to be used to add multiple cursors with the mouse. The Go to Definition and Open Link mouse gestures will adapt such that they do not conflict with the [multicursor modifier](https://code.visualstudio.com/docs/editor/codebasics#_multicursor-modifier)."
            ))
        }
    )))),
    multiCursorPaste: register(( (new EditorStringEnumOption(
        79 ,
        'multiCursorPaste',
        'spread',
        ['spread', 'full'],
        {
            markdownEnumDescriptions: [
                ( localizeWithPath(_moduleId, 295, "Each cursor pastes a single line of the text.")),
                ( localizeWithPath(_moduleId, 296, "Each cursor pastes the full text."))
            ],
            markdownDescription: ( localizeWithPath(
                _moduleId,
                297,
                "Controls pasting when the line count of the pasted text matches the cursor count."
            ))
        }
    )))),
    multiCursorLimit: register(( (new EditorIntOption(
        80 ,
        'multiCursorLimit',
        10000,
        1,
        100000,
        {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                298,
                "Controls the max number of cursors that can be in an active editor at once."
            ))
        }
    )))),
    occurrencesHighlight: register(( (new EditorStringEnumOption(
        81 ,
        'occurrencesHighlight',
        'singleFile',
        ['off', 'singleFile', 'multiFile'],
        {
            markdownEnumDescriptions: [
                ( localizeWithPath(_moduleId, 299, "Does not highlight occurrences.")),
                ( localizeWithPath(_moduleId, 300, "Highlights occurrences only in the current file.")),
                ( localizeWithPath(
                    _moduleId,
                    301,
                    "Experimental: Highlights occurrences across all valid open files."
                ))
            ],
            markdownDescription: ( localizeWithPath(
                _moduleId,
                302,
                "Controls whether occurrences should be highlighted across open files."
            ))
        }
    )))),
    overviewRulerBorder: register(( (new EditorBooleanOption(
        82 ,
        'overviewRulerBorder',
        true,
        { description: ( localizeWithPath(
            _moduleId,
            303,
            "Controls whether a border should be drawn around the overview ruler."
        )) }
    )))),
    overviewRulerLanes: register(( (new EditorIntOption(83 , 'overviewRulerLanes', 3, 0, 3)))),
    padding: register(( (new EditorPadding()))),
    pasteAs: register(( (new EditorPasteAs()))),
    parameterHints: register(( (new EditorParameterHints()))),
    peekWidgetDefaultFocus: register(( (new EditorStringEnumOption(
        87 ,
        'peekWidgetDefaultFocus',
        'tree',
        ['tree', 'editor'],
        {
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 304, "Focus the tree when opening peek")),
                ( localizeWithPath(_moduleId, 305, "Focus the editor when opening peek"))
            ],
            description: ( localizeWithPath(
                _moduleId,
                306,
                "Controls whether to focus the inline editor or the tree in the peek widget."
            ))
        }
    )))),
    definitionLinkOpensInPeek: register(( (new EditorBooleanOption(
        88 ,
        'definitionLinkOpensInPeek',
        false,
        { description: ( localizeWithPath(
            _moduleId,
            307,
            "Controls whether the Go to Definition mouse gesture always opens the peek widget."
        )) }
    )))),
    quickSuggestions: register(( (new EditorQuickSuggestions()))),
    quickSuggestionsDelay: register(( (new EditorIntOption(
        90 ,
        'quickSuggestionsDelay',
        10,
        0,
        1073741824 ,
        { description: ( localizeWithPath(
            _moduleId,
            308,
            "Controls the delay in milliseconds after which quick suggestions will show up."
        )) }
    )))),
    readOnly: register(( (new EditorBooleanOption(91 , 'readOnly', false)))),
    readOnlyMessage: register(( (new ReadonlyMessage()))),
    renameOnType: register(( (new EditorBooleanOption(
        93 ,
        'renameOnType',
        false,
        { description: ( localizeWithPath(_moduleId, 309, "Controls whether the editor auto renames on type.")), markdownDeprecationMessage: ( localizeWithPath(_moduleId, 310, "Deprecated, use `editor.linkedEditing` instead.")) }
    )))),
    renderControlCharacters: register(( (new EditorBooleanOption(
        94 ,
        'renderControlCharacters',
        true,
        { description: ( localizeWithPath(
            _moduleId,
            311,
            "Controls whether the editor should render control characters."
        )), restricted: true }
    )))),
    renderFinalNewline: register(( (new EditorStringEnumOption(
        95 ,
        'renderFinalNewline',
        (isLinux ? 'dimmed' : 'on'),
        ['off', 'on', 'dimmed'],
        { description: ( localizeWithPath(
            _moduleId,
            312,
            "Render last line number when the file ends with a newline."
        )) }
    )))),
    renderLineHighlight: register(( (new EditorStringEnumOption(
        96 ,
        'renderLineHighlight',
        'line',
        ['none', 'gutter', 'line', 'all'],
        {
            enumDescriptions: [
                '',
                '',
                '',
                ( localizeWithPath(_moduleId, 313, "Highlights both the gutter and the current line.")),
            ],
            description: ( localizeWithPath(
                _moduleId,
                314,
                "Controls how the editor should render the current line highlight."
            ))
        }
    )))),
    renderLineHighlightOnlyWhenFocus: register(( (new EditorBooleanOption(
        97 ,
        'renderLineHighlightOnlyWhenFocus',
        false,
        { description: ( localizeWithPath(
            _moduleId,
            315,
            "Controls if the editor should render the current line highlight only when the editor is focused."
        )) }
    )))),
    renderValidationDecorations: register(( (new EditorStringEnumOption(
        98 ,
        'renderValidationDecorations',
        'editable',
        ['editable', 'on', 'off']
    )))),
    renderWhitespace: register(( (new EditorStringEnumOption(
        99 ,
        'renderWhitespace',
        'selection',
        ['none', 'boundary', 'selection', 'trailing', 'all'],
        {
            enumDescriptions: [
                '',
                ( localizeWithPath(
                    _moduleId,
                    316,
                    "Render whitespace characters except for single spaces between words."
                )),
                ( localizeWithPath(_moduleId, 317, "Render whitespace characters only on selected text.")),
                ( localizeWithPath(_moduleId, 318, "Render only trailing whitespace characters.")),
                ''
            ],
            description: ( localizeWithPath(
                _moduleId,
                319,
                "Controls how the editor should render whitespace characters."
            ))
        }
    )))),
    revealHorizontalRightPadding: register(( (new EditorIntOption(
        100 ,
        'revealHorizontalRightPadding',
        15,
        0,
        1000
    )))),
    roundedSelection: register(( (new EditorBooleanOption(
        101 ,
        'roundedSelection',
        true,
        { description: ( localizeWithPath(_moduleId, 320, "Controls whether selections should have rounded corners.")) }
    )))),
    rulers: register(( (new EditorRulers()))),
    scrollbar: register(( (new EditorScrollbar()))),
    scrollBeyondLastColumn: register(( (new EditorIntOption(
        104 ,
        'scrollBeyondLastColumn',
        4,
        0,
        1073741824 ,
        { description: ( localizeWithPath(
            _moduleId,
            321,
            "Controls the number of extra characters beyond which the editor will scroll horizontally."
        )) }
    )))),
    scrollBeyondLastLine: register(( (new EditorBooleanOption(
        105 ,
        'scrollBeyondLastLine',
        true,
        { description: ( localizeWithPath(
            _moduleId,
            322,
            "Controls whether the editor will scroll beyond the last line."
        )) }
    )))),
    scrollPredominantAxis: register(( (new EditorBooleanOption(
        106 ,
        'scrollPredominantAxis',
        true,
        { description: ( localizeWithPath(
            _moduleId,
            323,
            "Scroll only along the predominant axis when scrolling both vertically and horizontally at the same time. Prevents horizontal drift when scrolling vertically on a trackpad."
        )) }
    )))),
    selectionClipboard: register(( (new EditorBooleanOption(107 , 'selectionClipboard', true, {
        description: ( localizeWithPath(
            _moduleId,
            324,
            "Controls whether the Linux primary clipboard should be supported."
        )),
        included: isLinux
    })))),
    selectionHighlight: register(( (new EditorBooleanOption(
        108 ,
        'selectionHighlight',
        true,
        { description: ( localizeWithPath(
            _moduleId,
            325,
            "Controls whether the editor should highlight matches similar to the selection."
        )) }
    )))),
    selectOnLineNumbers: register(( (new EditorBooleanOption(109 , 'selectOnLineNumbers', true)))),
    showFoldingControls: register(( (new EditorStringEnumOption(
        110 ,
        'showFoldingControls',
        'mouseover',
        ['always', 'never', 'mouseover'],
        {
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 326, "Always show the folding controls.")),
                ( localizeWithPath(
                    _moduleId,
                    327,
                    "Never show the folding controls and reduce the gutter size."
                )),
                ( localizeWithPath(
                    _moduleId,
                    328,
                    "Only show the folding controls when the mouse is over the gutter."
                )),
            ],
            description: ( localizeWithPath(
                _moduleId,
                329,
                "Controls when the folding controls on the gutter are shown."
            ))
        }
    )))),
    showUnused: register(( (new EditorBooleanOption(
        111 ,
        'showUnused',
        true,
        { description: ( localizeWithPath(_moduleId, 330, "Controls fading out of unused code.")) }
    )))),
    showDeprecated: register(( (new EditorBooleanOption(
        140 ,
        'showDeprecated',
        true,
        { description: ( localizeWithPath(_moduleId, 331, "Controls strikethrough deprecated variables.")) }
    )))),
    inlayHints: register(( (new EditorInlayHints()))),
    snippetSuggestions: register(( (new EditorStringEnumOption(
        112 ,
        'snippetSuggestions',
        'inline',
        ['top', 'bottom', 'inline', 'none'],
        {
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 332, "Show snippet suggestions on top of other suggestions.")),
                ( localizeWithPath(_moduleId, 333, "Show snippet suggestions below other suggestions.")),
                ( localizeWithPath(_moduleId, 334, "Show snippets suggestions with other suggestions.")),
                ( localizeWithPath(_moduleId, 335, "Do not show snippet suggestions.")),
            ],
            description: ( localizeWithPath(
                _moduleId,
                336,
                "Controls whether snippets are shown with other suggestions and how they are sorted."
            ))
        }
    )))),
    smartSelect: register(( (new SmartSelect()))),
    smoothScrolling: register(( (new EditorBooleanOption(
        114 ,
        'smoothScrolling',
        false,
        { description: ( localizeWithPath(
            _moduleId,
            337,
            "Controls whether the editor will scroll using an animation."
        )) }
    )))),
    stopRenderingLineAfter: register(( (new EditorIntOption(
        117 ,
        'stopRenderingLineAfter',
        10000,
        -1,
        1073741824
    )))),
    suggest: register(( (new EditorSuggest()))),
    inlineSuggest: register(( (new InlineEditorSuggest()))),
    inlineEdit: register(( (new InlineEditorEdit()))),
    inlineCompletionsAccessibilityVerbose: register(( (new EditorBooleanOption(
        149 ,
        'inlineCompletionsAccessibilityVerbose',
        false,
        { description: ( localizeWithPath(
            _moduleId,
            338,
            "Controls whether the accessibility hint should be provided to screen reader users when an inline completion is shown."
        )) }
    )))),
    suggestFontSize: register(( (new EditorIntOption(
        119 ,
        'suggestFontSize',
        0,
        0,
        1000,
        { markdownDescription: ( localizeWithPath(
            _moduleId,
            339,
            "Font size for the suggest widget. When set to {0}, the value of {1} is used.",
            '`0`',
            '`#editor.fontSize#`'
        )) }
    )))),
    suggestLineHeight: register(( (new EditorIntOption(
        120 ,
        'suggestLineHeight',
        0,
        0,
        1000,
        { markdownDescription: ( localizeWithPath(
            _moduleId,
            340,
            "Line height for the suggest widget. When set to {0}, the value of {1} is used. The minimum value is 8.",
            '`0`',
            '`#editor.lineHeight#`'
        )) }
    )))),
    suggestOnTriggerCharacters: register(( (new EditorBooleanOption(
        121 ,
        'suggestOnTriggerCharacters',
        true,
        { description: ( localizeWithPath(
            _moduleId,
            341,
            "Controls whether suggestions should automatically show up when typing trigger characters."
        )) }
    )))),
    suggestSelection: register(( (new EditorStringEnumOption(
        122 ,
        'suggestSelection',
        'first',
        ['first', 'recentlyUsed', 'recentlyUsedByPrefix'],
        {
            markdownEnumDescriptions: [
                ( localizeWithPath(_moduleId, 342, "Always select the first suggestion.")),
                ( localizeWithPath(
                    _moduleId,
                    343,
                    "Select recent suggestions unless further typing selects one, e.g. `console.| -> console.log` because `log` has been completed recently."
                )),
                ( localizeWithPath(
                    _moduleId,
                    344,
                    "Select suggestions based on previous prefixes that have completed those suggestions, e.g. `co -> console` and `con -> const`."
                )),
            ],
            description: ( localizeWithPath(
                _moduleId,
                345,
                "Controls how suggestions are pre-selected when showing the suggest list."
            ))
        }
    )))),
    tabCompletion: register(( (new EditorStringEnumOption(
        123 ,
        'tabCompletion',
        'off',
        ['on', 'off', 'onlySnippets'],
        {
            enumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    346,
                    "Tab complete will insert the best matching suggestion when pressing tab."
                )),
                ( localizeWithPath(_moduleId, 347, "Disable tab completions.")),
                ( localizeWithPath(
                    _moduleId,
                    348,
                    "Tab complete snippets when their prefix match. Works best when 'quickSuggestions' aren't enabled."
                )),
            ],
            description: ( localizeWithPath(_moduleId, 349, "Enables tab completions."))
        }
    )))),
    tabIndex: register(( (new EditorIntOption(
        124 ,
        'tabIndex',
        0,
        -1,
        1073741824
    )))),
    unicodeHighlight: register(( (new UnicodeHighlight()))),
    unusualLineTerminators: register(( (new EditorStringEnumOption(
        126 ,
        'unusualLineTerminators',
        'prompt',
        ['auto', 'off', 'prompt'],
        {
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 350, "Unusual line terminators are automatically removed.")),
                ( localizeWithPath(_moduleId, 351, "Unusual line terminators are ignored.")),
                ( localizeWithPath(_moduleId, 352, "Unusual line terminators prompt to be removed.")),
            ],
            description: ( localizeWithPath(
                _moduleId,
                353,
                "Remove unusual line terminators that might cause problems."
            ))
        }
    )))),
    useShadowDOM: register(( (new EditorBooleanOption(127 , 'useShadowDOM', true)))),
    useTabStops: register(( (new EditorBooleanOption(
        128 ,
        'useTabStops',
        true,
        { description: ( localizeWithPath(
            _moduleId,
            354,
            "Spaces and tabs are inserted and deleted in alignment with tab stops."
        )) }
    )))),
    wordBreak: register(( (new EditorStringEnumOption(
        129 ,
        'wordBreak',
        'normal',
        ['normal', 'keepAll'],
        {
            markdownEnumDescriptions: [
                ( localizeWithPath(_moduleId, 355, "Use the default line break rule.")),
                ( localizeWithPath(
                    _moduleId,
                    356,
                    "Word breaks should not be used for Chinese/Japanese/Korean (CJK) text. Non-CJK text behavior is the same as for normal."
                )),
            ],
            description: ( localizeWithPath(
                _moduleId,
                357,
                "Controls the word break rules used for Chinese/Japanese/Korean (CJK) text."
            ))
        }
    )))),
    wordSegmenterLocales: register(( (new WordSegmenterLocales()))),
    wordSeparators: register(( (new EditorStringOption(
        131 ,
        'wordSeparators',
        USUAL_WORD_SEPARATORS,
        { description: ( localizeWithPath(
            _moduleId,
            358,
            "Characters that will be used as word separators when doing word related navigations or operations."
        )) }
    )))),
    wordWrap: register(( (new EditorStringEnumOption(
        132 ,
        'wordWrap',
        'off',
        ['off', 'on', 'wordWrapColumn', 'bounded'],
        {
            markdownEnumDescriptions: [
                ( localizeWithPath(_moduleId, 359, "Lines will never wrap.")),
                ( localizeWithPath(_moduleId, 360, "Lines will wrap at the viewport width.")),
                ( localizeWithPath(_moduleId, 361, "Lines will wrap at `#editor.wordWrapColumn#`.")),
                ( localizeWithPath(
                    _moduleId,
                    362,
                    "Lines will wrap at the minimum of viewport and `#editor.wordWrapColumn#`."
                )),
            ],
            description: ( localizeWithPath(_moduleId, 363, "Controls how lines should wrap."))
        }
    )))),
    wordWrapBreakAfterCharacters: register(( (new EditorStringOption(
        133 ,
        'wordWrapBreakAfterCharacters',
        ' \t})]?|/&.,;¢°′″‰℃、。｡､￠，．：；？！％・･ゝゞヽヾーァィゥェォッャュョヮヵヶぁぃぅぇぉっゃゅょゎゕゖㇰㇱㇲㇳㇴㇵㇶㇷㇸㇹㇺㇻㇼㇽㇾㇿ々〻ｧｨｩｪｫｬｭｮｯｰ”〉》」』】〕）］｝｣'
    )))),
    wordWrapBreakBeforeCharacters: register(( (new EditorStringOption(
        134 ,
        'wordWrapBreakBeforeCharacters',
        '([{‘“〈《「『【〔（［｛｢£¥＄￡￥+＋'
    )))),
    wordWrapColumn: register(( (new EditorIntOption(
        135 ,
        'wordWrapColumn',
        80,
        1,
        1073741824 ,
        {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                364,
                "Controls the wrapping column of the editor when `#editor.wordWrap#` is `wordWrapColumn` or `bounded`."
            ))
        }
    )))),
    wordWrapOverride1: register(( (new EditorStringEnumOption(
        136 ,
        'wordWrapOverride1',
        'inherit',
        ['off', 'on', 'inherit']
    )))),
    wordWrapOverride2: register(( (new EditorStringEnumOption(
        137 ,
        'wordWrapOverride2',
        'inherit',
        ['off', 'on', 'inherit']
    )))),
    editorClassName: register(( (new EditorClassName()))),
    defaultColorDecorators: register(( (new EditorBooleanOption(
        147 ,
        'defaultColorDecorators',
        false,
        { markdownDescription: ( localizeWithPath(
            _moduleId,
            365,
            "Controls whether inline color decorations should be shown using the default document color provider"
        )) }
    )))),
    pixelRatio: register(( (new EditorPixelRatio()))),
    tabFocusMode: register(( (new EditorBooleanOption(
        144 ,
        'tabFocusMode',
        false,
        { markdownDescription: ( localizeWithPath(
            _moduleId,
            366,
            "Controls whether the editor receives tabs or defers them to the workbench for navigation."
        )) }
    )))),
    layoutInfo: register(( (new EditorLayoutInfoComputer()))),
    wrappingInfo: register(( (new EditorWrappingInfoComputer()))),
    wrappingIndent: register(( (new WrappingIndentOption()))),
    wrappingStrategy: register(( (new WrappingStrategy())))
};
export { ApplyUpdateResult, ComputeOptionsMemory, ConfigurationChangedEvent, EDITOR_FONT_DEFAULTS, EditorFontLigatures, EditorFontVariations, EditorLayoutInfoComputer, EditorOptions, MINIMAP_GUTTER_WIDTH, ShowLightbulbIconMode, TextEditorCursorStyle, boolean, clampedFloat, clampedInt, cursorStyleToString, editorOptionsRegistry, filterValidationDecorations, inUntrustedWorkspace, stringSet, unicodeHighlightConfigKeys };
